import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { SecondaryBase } from './../Commons/SecondaryBase'

import { LyricsGenerator } from './Karaoke/LyricsGenerator'
import { KaraokeSongCreator } from './Karaoke/KaraokeSongCreator'

import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
import { ColorSettingsModal } from './Karaoke/ColorSettingsModal'

// NUEVO: Importa la función de tu BL para el Overlay (Asegúrate de que la ruta coincida)
import { UpdateKaraokeCamOverlay } from './../../../utilities/BL/Admin/Games/Karaoke'

/**
 * ==============================================================================
 * HELPERS (Cálculos de tiempo)
 * ==============================================================================
 */
const getBlockDurationMs = (block) => {
  if (!block) return 0;
  if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0);
  return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
};

const getBlockDisplayText = (block) => {
  if (!block) return "";
  if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : "";
  if (block.mode === 'uniform') return block.text || "";
  return (block.parts || []).map(p => p.text || "").join("");
};

// Valores por defecto para colores
const DEFAULT_COLORS = { base: '#ffffff', fill: '#ffd400', stroke: '#000000' };

/**
 * ==============================================================================
 * COMPONENTE: KARAOKE DASHBOARD
 * ==============================================================================
 */
const Karaoke = ({ getOutOfTheDynamic }) => {
  const navigate = useNavigate();
  const { showMessage } = useFloatMessage();

  // --------------------------------------------------------------------------------
  // 1. ESTADOS DE DATOS
  // --------------------------------------------------------------------------------
  const [songs, setSongs] = useState([]);               
  const [selectedSongId, setSelectedSongId] = useState(""); 

  // --------------------------------------------------------------------------------
  // 2. ESTADOS DEL REPRODUCTOR
  // --------------------------------------------------------------------------------
  const [isPlaying, setIsPlaying] = useState(false);        
  const [activeBlockIndex, setActiveBlockIndex] = useState(0); 
  const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0); 

  // --------------------------------------------------------------------------------
  // 3. ESTADOS DE CONFIGURACIÓN Y UI
  // --------------------------------------------------------------------------------
  const [isColorModalOpen, setIsColorModalOpen] = useState(false); 
  
  // Estado de los botones toggle
  const [isFillingEnabled, setIsFillingEnabled] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  const [colorSettings, setColorSettings] = useState(DEFAULT_COLORS);

  // --------------------------------------------------------------------------------
  // NUEVO 3.1: ESTADOS DE LA KARAOKE CAM (OVERLAY)
  // --------------------------------------------------------------------------------
  // Visibilidad
  // const [showMic, setShowMic] = useState(false);
  // const [showNotes, setShowNotes] = useState(false);
  // const [showText, setShowText] = useState(false);

  // Posiciones (Para resolución 1920x1080)
  // const [micX, setMicX] = useState(500);
  // const [micY, setMicY] = useState(100);
  // const [micRotation, setMicRotation] = useState(0);

  const [textNotesX, setTextNotesX] = useState(800);
  const [textNotesY, setTextNotesY] = useState(800);

  //const [isCamSyncing, setIsCamSyncing] = useState(false);

  // Físicas Trackpad
  // const [isDraggingMic, setIsDraggingMic] = useState(false);
  // const dragMicAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  // const [isDraggingText, setIsDraggingText] = useState(false);
  // const dragTextAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const MAX_X_MIC = 1700;
  const MAX_Y_MIC = 1000;
  const MAX_X_TEXT = 1500;
  const MAX_Y_TEXT = 1000;

  // --------------------------------------------------------------------------------
  // 4. REFERENCIAS
  // --------------------------------------------------------------------------------
  const rafIdRef = useRef(null);       
  const lastFrameTimeRef = useRef(0);  
  const lyricsListRef = useRef(null);  

  // --------------------------------------------------------------------------------
  // 5. COMPUTADOS
  // --------------------------------------------------------------------------------
  const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
  const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
  const activeBlock = blocks[activeBlockIndex] || null;
  const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);

  // Progreso visual local (Preview)
  const fillProgress = useMemo(() => {
    if (!isFillingEnabled) return 0; 
    const total = getBlockDurationMs(activeBlock);
    if (total <= 0) return 0;
    if (activeBlock?.type === 'instrumental') return 0; 
    return Math.min(Math.max(elapsedInBlockMs, 0), total) / total;
  }, [activeBlock, elapsedInBlockMs, isFillingEnabled]);

  // Leyenda de estatus
  const statusLegend = useMemo(() => {
    if (!selectedSongId) return "Sin canción seleccionada";
    if (isPlaying) return `En reproducción - Línea #${activeBlockIndex + 1}`;
    if (activeBlockIndex > 0 && activeBlockIndex < blocks.length) return `Pausado - Línea #${activeBlockIndex + 1}`;
    if (activeBlockIndex === 0 && !isPlaying) return "Detenido (Inicio)";
    return "Listo";
  }, [selectedSongId, isPlaying, activeBlockIndex, blocks.length]);




  // Visibilidad
  const [showMic, setShowMic] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showText, setShowText] = useState(false);

  // Estados Independientes
  const [micX, setMicX] = useState(500);
  const [micY, setMicY] = useState(100);
  const [micRotation, setMicRotation] = useState(0);
  const [micScale, setMicScale] = useState(1.0);

  const [notesX, setNotesX] = useState(800);
  const [notesY, setNotesY] = useState(600);
  const [notesScale, setNotesScale] = useState(1.0);

  const [textX, setTextX] = useState(300);
  const [textY, setTextY] = useState(200);
  const [textScale, setTextScale] = useState(1.0);
  const [customText, setCustomText] = useState("¡Canta con nosotros!");

  const [isCamSyncing, setIsCamSyncing] = useState(false);

  // Físicas Trackpads
  const [isDraggingMic, setIsDraggingMic] = useState(false);
  const dragMicAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const [isDraggingNotes, setIsDraggingNotes] = useState(false);
  const dragNotesAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const [isDraggingText, setIsDraggingText] = useState(false);
  const dragTextAnchor = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  // Topes
  const MAX_X = 1700;
  const MAX_Y = 1000;


  // --------------------------------------------------------------------------------
  // 6. CICLO DE VIDA: CARGA INICIAL Y KARAOKE CAM SYNC
  // --------------------------------------------------------------------------------
  
  useEffect(() => {
    const loadSongs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`);
            if (response.ok) {
                const data = await response.json();
                setSongs(data.songs || []);
            }
        } catch (error) { console.error("Error cargando canciones", error); }
    };
    loadSongs();
  }, []);

  // Resetear player al cambiar canción
  useEffect(() => {
    stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSongId]);

  // NUEVO: Sincronización en vivo del Overlay de Cam (Debounce)
  // useEffect(() => {
  //     setIsCamSyncing(true);
  //     const debounceTimer = setTimeout(async () => {
  //         const configPayload = {
  //             showMic, showNotes, showText,
  //             micX, micY, micRotation,
  //             textNotesX, textNotesY
  //         };
  //         try {
  //             await UpdateKaraokeCamOverlay(configPayload);
  //             setIsCamSyncing(false);
  //         } catch (e) {
  //             console.error(e);
  //             setIsCamSyncing(false);
  //         }
  //     }, 150);
  //     return () => clearTimeout(debounceTimer);
  // }, [showMic, showNotes, showText, micX, micY, micRotation, textNotesX, textNotesY]);

  useEffect(() => {
      setIsCamSyncing(true);
      const debounceTimer = setTimeout(async () => {
          const configPayload = {
              showMic, showNotes, showText,
              micX, micY, micRotation, micScale,
              notesX, notesY, notesScale,
              textX, textY, textScale, customText
          };
          try {
              await UpdateKaraokeCamOverlay(configPayload);
              setIsCamSyncing(false);
          } catch (e) {
              console.error(e);
              setIsCamSyncing(false);
          }
      }, 150);
      return () => clearTimeout(debounceTimer);
  }, [showMic, showNotes, showText, micX, micY, micRotation, micScale, notesX, notesY, notesScale, textX, textY, textScale, customText]);


  // --------------------------------------------------------------------------------
  // NUEVO 6.1 MÉTODOS DE TRACKPAD (MIC Y TEXTO)
  // --------------------------------------------------------------------------------

  // // --- Trackpad MICRÓFONO ---
  // const handleMicPointerDown = (e) => {
  //     setIsDraggingMic(true);
  //     dragMicAnchor.current = {
  //         x: e.clientX || (e.touches && e.touches[0].clientX),
  //         y: e.clientY || (e.touches && e.touches[0].clientY),
  //         startX: micX,
  //         startY: micY
  //     };
  //     if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
  // };
  // const handleMicPointerMove = (e) => {
  //     if (!isDraggingMic) return;
  //     const curX = e.clientX || (e.touches && e.touches[0].clientX);
  //     const curY = e.clientY || (e.touches && e.touches[0].clientY);
  //     const deltaX = curX - dragMicAnchor.current.x;
  //     const deltaY = curY - dragMicAnchor.current.y;
      
  //     let newX = Math.round(dragMicAnchor.current.startX - (deltaX * 3));
  //     let newY = Math.round(dragMicAnchor.current.startY - (deltaY * 3));
      
  //     setMicX(Math.max(-200, Math.min(MAX_X_MIC, newX)));
  //     setMicY(Math.max(-200, Math.min(MAX_Y_MIC, newY)));
  // };
  // const handleMicPointerUp = () => setIsDraggingMic(false);

  // // --- Trackpad TEXTO/NOTAS ---
  // const handleTextPointerDown = (e) => {
  //     setIsDraggingText(true);
  //     dragTextAnchor.current = {
  //         x: e.clientX || (e.touches && e.touches[0].clientX),
  //         y: e.clientY || (e.touches && e.touches[0].clientY),
  //         startX: textNotesX,
  //         startY: textNotesY
  //     };
  //     if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
  // };
  // const handleTextPointerMove = (e) => {
  //     if (!isDraggingText) return;
  //     const curX = e.clientX || (e.touches && e.touches[0].clientX);
  //     const curY = e.clientY || (e.touches && e.touches[0].clientY);
  //     const deltaX = curX - dragTextAnchor.current.x;
  //     const deltaY = curY - dragTextAnchor.current.y;
      
  //     let newX = Math.round(dragTextAnchor.current.startX - (deltaX * 3));
  //     let newY = Math.round(dragTextAnchor.current.startY - (deltaY * 3));
      
  //     setTextNotesX(Math.max(-500, Math.min(MAX_X_TEXT, newX)));
  //     setTextNotesY(Math.max(-200, Math.min(MAX_Y_TEXT, newY)));
  // };
  // const handleTextPointerUp = () => setIsDraggingText(false);

    // --- Trackpad MICRÓFONO ---
    const handleMicPointerDown = (e) => {
        setIsDraggingMic(true); dragMicAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: micX, startY: micY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleMicPointerMove = (e) => {
        if (!isDraggingMic) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragMicAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragMicAnchor.current.y;
        setMicX(Math.max(-200, Math.min(MAX_X, Math.round(dragMicAnchor.current.startX - (deltaX * 3)))));
        setMicY(Math.max(-200, Math.min(MAX_Y, Math.round(dragMicAnchor.current.startY - (deltaY * 3)))));
    };
    const handleMicPointerUp = () => setIsDraggingMic(false);

    // --- Trackpad NOTAS ---
    const handleNotesPointerDown = (e) => {
        setIsDraggingNotes(true); dragNotesAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: notesX, startY: notesY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleNotesPointerMove = (e) => {
        if (!isDraggingNotes) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragNotesAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragNotesAnchor.current.y;
        setNotesX(Math.max(-200, Math.min(MAX_X, Math.round(dragNotesAnchor.current.startX - (deltaX * 3)))));
        setNotesY(Math.max(-200, Math.min(MAX_Y, Math.round(dragNotesAnchor.current.startY - (deltaY * 3)))));
    };
    const handleNotesPointerUp = () => setIsDraggingNotes(false);

    // --- Trackpad TEXTO ---
    const handleTextPointerDown = (e) => {
        setIsDraggingText(true); dragTextAnchor.current = { x: e.clientX || e.touches[0].clientX, y: e.clientY || e.touches[0].clientY, startX: textX, startY: textY };
        if (e.target.setPointerCapture) e.target.setPointerCapture(e.pointerId);
    };
    const handleTextPointerMove = (e) => {
        if (!isDraggingText) return;
        const deltaX = (e.clientX || e.touches[0].clientX) - dragTextAnchor.current.x;
        const deltaY = (e.clientY || e.touches[0].clientY) - dragTextAnchor.current.y;
        setTextX(Math.max(-800, Math.min(MAX_X, Math.round(dragTextAnchor.current.startX - (deltaX * 3)))));
        setTextY(Math.max(-200, Math.min(MAX_Y, Math.round(dragTextAnchor.current.startY - (deltaY * 3)))));
    };
    const handleTextPointerUp = () => setIsDraggingText(false);


  // --------------------------------------------------------------------------------
  // 7. COMUNICACIÓN API (CONTROL DE PANTALLA)
  // --------------------------------------------------------------------------------

  const handleToggleVisible = async () => {
    const newValue = !isVisible;
    setIsVisible(newValue); 
    try { 
        await fetch(`${process.env.REACT_APP_API_URL}karaoke/visible?visible=${newValue}`, { method: 'POST' }); 
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Letras mostradas en pantalla' : 'Letras ocultas' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión' }); }
  };

  const handleToggleFilling = async () => {
    const newValue = !isFillingEnabled;
    setIsFillingEnabled(newValue); 
    try { 
        await fetch(`${process.env.REACT_APP_API_URL}karaoke/fill?fill=${newValue}`, { method: 'POST' }); 
        if (activeBlock) { sendCurrentLyricToScreen(activeBlock, newValue); }
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Rellenado activado' : 'Rellenado desactivado' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error' }); }
  };

  const handleSaveColors = async (newColors) => {
    setColorSettings(newColors);
    try {
      await fetch(`${process.env.REACT_APP_API_URL}karaoke/colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ BaseColor: newColors.base, FillColor: newColors.fill, StrokeColor: newColors.stroke })
      });
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Colores actualizados correctamente' });
    } catch (e) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al guardar colores' }); }
  };

  const handleResetColors = () => {
      handleSaveColors(DEFAULT_COLORS);
      setColorSettings(DEFAULT_COLORS); 
  };

  const handleDeleteSong = async () => {
    if (!selectedSongId) return;
    if (!window.confirm("¿Seguro que deseas eliminar esta canción?")) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs/${selectedSongId}`, { method: 'DELETE' });
      if (response.ok) {
        setSongs(prev => prev.filter(s => s.id !== selectedSongId));
        setSelectedSongId(""); 
        stopPlayback(); 
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción eliminada' });
      } else { showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo eliminar' }); }
    } catch (error) { showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de red' }); }
  };

  const sendCurrentLyricToScreen = async (block, forceFilling = null) => {
    if (!block) return;
    const payload = {
      text: block.mode === 'uniform' ? block.text : block.parts?.map(p => p.text).join("") || "",
      mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
      duration: getBlockDurationMs(block)
    };
    try {
      await fetch(`${process.env.REACT_APP_API_URL}karaoke/lyric`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    } catch (e) { console.error("Error enviando lyric:", e); }
  };

  useEffect(() => {
    if (activeBlock) {
      sendCurrentLyricToScreen(activeBlock);
      scrollToActiveLyricInList(); 
    }
  }, [activeBlockIndex]);


  // --------------------------------------------------------------------------------
  // 8. FUNCIONES DE LISTA (SCROLL)
  // --------------------------------------------------------------------------------

  const scrollToActiveLyricInList = () => {
    if (lyricsListRef.current) {
      const listItems = lyricsListRef.current.children;
      const activeItem = listItems[activeBlockIndex];
      if (activeItem) { activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    }
  };

  const scrollListUp = () => { if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: -100, behavior: 'smooth' }); };
  const scrollListDown = () => { if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: 100, behavior: 'smooth' }); };

  // --------------------------------------------------------------------------------
  // 9. CONTROLES DE REPRODUCCIÓN
  // --------------------------------------------------------------------------------

  const playPlayback = () => {
    if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona una canción válida.' });
    setIsPlaying(true);
    if (activeBlock) { sendCurrentLyricToScreen(activeBlock); }
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reproduciendo...' });
  };

  const pausePlayback = () => {
    setIsPlaying(false);
    lastFrameTimeRef.current = 0;
    showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Pausado' });
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    setActiveBlockIndex(0);
    setElapsedInBlockMs(0);
    lastFrameTimeRef.current = 0;
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
  };

  const goToNext = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
  };

  const goToPrev = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
  };
  
  const goToStart = () => { 
      stopPlayback(); 
      showMessage({ type: MESSAGE_TYPE.INFO, text: 'Regresando al inicio' });
  };
  
  const goToEnd = () => {
    setElapsedInBlockMs(0);
    setActiveBlockIndex(blocks.length - 1);
    showMessage({ type: MESSAGE_TYPE.INFO, text: 'Saltando al final' });
  };

  const handleJumpToLine = (index) => {
      setElapsedInBlockMs(0);
      setActiveBlockIndex(index);
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Saltando a línea #${index + 1}` });
  };

  // --------------------------------------------------------------------------------
  // 10. LOOP DE ANIMACIÓN
  // --------------------------------------------------------------------------------
  useEffect(() => {
    if (!isPlaying || !activeBlock) return;

    const tick = (now) => {
      if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
      const deltaMs = now - lastFrameTimeRef.current;
      lastFrameTimeRef.current = now;

      setElapsedInBlockMs(prev => {
        const duration = getBlockDurationMs(activeBlock);
        const nextElapsed = prev + deltaMs;

        if (duration > 0 && nextElapsed >= duration) {
          const nextIdx = activeBlockIndex + 1;
          if (nextIdx >= blocks.length) {
            setIsPlaying(false); 
            return duration; 
          }
          setActiveBlockIndex(nextIdx); 
          lastFrameTimeRef.current = 0; 
          return 0;
        }
        return nextElapsed;
      });
      rafIdRef.current = requestAnimationFrame(tick);
    };
    rafIdRef.current = requestAnimationFrame(tick);
    return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
  }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);

  const ReturnToDashboard = async () => {
    try {
      await BLResetCurrentDynamic(); 
      getOutOfTheDynamic();
    } catch (error) { }
  }


  // --------------------------------------------------------------------------------
  // 11. RENDERIZADO
  // --------------------------------------------------------------------------------
  return (
    <SecondaryBase actionToExecute={ReturnToDashboard}>
      <div className='Karaoke'>
        
        <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

        <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
          <i className='bi bi-plus-circle'></i> Crear canción
        </button>

        <div className='Karaoke__Container mb-4'>
          
          {/* === PANEL DECK === */}
          <div className='Karaoke__Container__Deck Element--Shadow'>
            
            {/* A. Botones de Configuración */}
            <div className='Karaoke__Container__Deck__SettingButtons'>
              {selectedSongId && (
                <button 
                    type="button" 
                    className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'
                    onClick={handleDeleteSong}
                    title="Borrar canción"
                >
                  <i className='bi bi-trash'></i> Borrar canción
                </button>
              )}

              {/* Botón Rellenar */}
              <button 
                type='button' 
                className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isFillingEnabled ? 'Btn__Adsl--Red' : ''}`} 
                onClick={handleToggleFilling}
                title={isFillingEnabled ? "Desactivar Relleno" : "Activar Relleno"}
              >
                <i className='bi bi-paint-bucket'></i> {isFillingEnabled ? 'Sin relleno' : 'Rellenar'}
              </button>
              
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => setIsColorModalOpen(true)}>
                <i className='bi bi-palette'></i> Colores
              </button>
              
              {/* Botón Ocultar/Mostrar */}
              <button 
                type='button' 
                className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isVisible ? 'Btn__Adsl--Red' : ''}`} 
                onClick={handleToggleVisible}
                title={isVisible ? "Ocultar en Pantalla" : "Mostrar en Pantalla"}
              >
                <i className={`bi ${isVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i> {isVisible ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>

            {/* B. Playlist + Leyenda */}
            <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                    <select 
                        className='Karaoke__Container__Deck__Playlist' 
                        value={selectedSongId} 
                        onChange={(e) => setSelectedSongId(e.target.value)}
                        style={{ flex: 1 }}
                    >
                        <option value="">-- Selecciona una canción --</option>
                        {songs.map(song => (
                            <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
                        ))}
                    </select>
                </div>
                <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#666", marginTop: 5, fontWeight: "bold" }}>
                    {statusLegend}
                </div>
            </div>

            {/* C. Preview */}
            <div className='Karaoke__Container__Deck__Screen'>
               <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                 <div style={{ position: "relative", textAlign: "center", fontSize: 24, fontWeight: "900" }}>
                    <div style={{ color: colorSettings.base, WebkitTextStroke: `1px ${colorSettings.stroke}` }}>
                        {displayText || "..."}
                    </div>
                    {/* Visualización local del relleno: obedece al estado isFillingEnabled */}
                    {isFillingEnabled && (
                        <div style={{ 
                            position: "absolute", inset: 0, overflow: "hidden", whiteSpace: "nowrap",
                            width: `${(fillProgress * 100).toFixed(2)}%`,
                            color: colorSettings.fill, 
                            WebkitTextStroke: `1px ${colorSettings.stroke}`
                        }}>
                            {displayText || ""}
                        </div>
                    )}
                 </div>
               </div>
            </div>

            {/* D. Controles Playback */}
            <div className='Karaoke__Container__Deck__ControlButtons'>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Línea Anterior"><i className='bi bi-arrow-left'></i></button>
              
              {!isPlaying ? (
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
              ) : (
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
              )}

              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Línea Siguiente"><i className='bi bi-arrow-right'></i></button>
              <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
            </div>
          </div>

          {/* === PANEL LYRICS === */}
          <div className='Karaoke__Container__Lyrics Element--Shadow'>
            {!selectedSong ? (
               <p style={{textAlign:"center", color:"#999", marginTop: 20}}>Selecciona una canción.</p>
            ) : (
               <>
                 <div style={{textAlign:"center", marginBottom: 5, fontWeight:"bold", fontSize:"0.8rem"}}>
                    Total Líneas: {blocks.length}
                 </div>

                 {/* Botón Scroll Arriba */}
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListUp}>
                    <i className='bi bi-chevron-up'></i>
                 </button>

                 <div className='Karaoke__Container__Lyrics__Box'>
                   <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
                     {blocks.map((block, index) => {
                       const text = block.type === 'instrumental' 
                          ? '🎵 (Instrumental)' 
                          : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""));
                       
                       const isActive = index === activeBlockIndex;

                       return (
                         <li 
                           key={block.id || index} 
                           className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
                           onClick={() => handleJumpToLine(index)}
                         >
                           <strong>#{index + 1}</strong> - {text}
                         </li>
                       );
                     })}
                   </ul>
                 </div>

                 {/* Botón Scroll Abajo */}
                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListDown}>
                    <i className='bi bi-chevron-down'></i>
                 </button>
               </>
            )}
          </div>
        </div>

        {/* ===================== SECCIÓN EXCLUSIVA: OVERLAY KARAOKE CAM ===================== */}
        <div className="card shadow-sm border-info mt-5" style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <div className="card-header bg-info text-white font-weight-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-camera-video-fill me-2"></i> Karaoke Cam Overlay (OBS)</span>
                <span className={`badge ${isCamSyncing ? 'bg-warning text-dark' : 'bg-success'}`}>
                    {isCamSyncing ? 'Sincronizando...' : 'En Vivo'}
                </span>
            </div>
            <div className="card-body">
                
                {/* SWITCHES GENERALES */}
                <div className="d-flex justify-content-center flex-wrap gap-4 mb-4 p-3 bg-light rounded border">
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showMic} onChange={(e) => setShowMic(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎤 Micrófono</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎵 Notas</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🔤 Texto</label>
                    </div>
                    <button className="btn btn-outline-danger btn-sm font-weight-bold" onClick={() => {setShowMic(false); setShowNotes(false); setShowText(false);}}>Ocultar Todo</button>
                </div>

                <div className="row g-4">
                    {/* --- COLUMNA 1: MICRÓFONO --- */}
                    <div className="col-md-4 border-end">
                        <h6 className="font-weight-bold text-primary border-bottom pb-2 mb-3 text-center">🎤 Micrófono</h6>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Rotación:</span><span>{micRotation}°</span></label><input type="range" className="form-range" min="-90" max="90" step="5" value={micRotation} onChange={(e) => setMicRotation(parseInt(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{micScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={micScale} onChange={(e) => setMicScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{micX}px</span></label><input type="range" className="form-range" min="-200" max={MAX_X} step="10" value={micX} onChange={(e) => setMicX(parseInt(e.target.value))} /></div>
                        <div className="mb-3"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{micY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={micY} onChange={(e) => setMicY(parseInt(e.target.value))} /></div>
                        
                        <div className="d-flex justify-content-center">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #0d6efd', touchAction: 'none', cursor: isDraggingMic ? 'grabbing' : 'grab' }} onPointerDown={handleMicPointerDown} onPointerMove={handleMicPointerMove} onPointerUp={handleMicPointerUp} onPointerCancel={handleMicPointerUp} onPointerLeave={handleMicPointerUp}>
                                <div style={{ position: 'absolute', right: `${(micX / MAX_X) * 90}%`, bottom: `${(micY / MAX_Y) * 80}%`, width: '10%', height: '30%', backgroundColor: isDraggingMic ? '#0d6efd' : 'rgba(13, 110, 253, 0.5)', borderRadius: '10px', transform: `scale(${micScale}) rotate(${micRotation}deg)`, transformOrigin: 'bottom center', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* --- COLUMNA 2: NOTAS --- */}
                    <div className="col-md-4 border-end">
                        <h6 className="font-weight-bold text-success border-bottom pb-2 mb-3 text-center">🎵 Notas Musicales</h6>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{notesScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={notesScale} onChange={(e) => setNotesScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{notesX}px</span></label><input type="range" className="form-range" min="-200" max={MAX_X} step="10" value={notesX} onChange={(e) => setNotesX(parseInt(e.target.value))} /></div>
                        <div className="mb-3 mt-4"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{notesY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={notesY} onChange={(e) => setNotesY(parseInt(e.target.value))} /></div>
                        
                        <div className="d-flex justify-content-center mt-5">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #198754', touchAction: 'none', cursor: isDraggingNotes ? 'grabbing' : 'grab' }} onPointerDown={handleNotesPointerDown} onPointerMove={handleNotesPointerMove} onPointerUp={handleNotesPointerUp} onPointerCancel={handleNotesPointerUp} onPointerLeave={handleNotesPointerUp}>
                                <div style={{ position: 'absolute', right: `${(notesX / MAX_X) * 90}%`, bottom: `${(notesY / MAX_Y) * 80}%`, width: '20px', height: '20px', backgroundColor: isDraggingNotes ? '#198754' : 'rgba(25, 135, 84, 0.5)', borderRadius: '50%', transform: `scale(${notesScale})`, pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                    </div>

                    {/* --- COLUMNA 3: TEXTO DINÁMICO --- */}
                    <div className="col-md-4">
                        <h6 className="font-weight-bold text-danger border-bottom pb-2 mb-3 text-center">🔤 Texto</h6>
                        <div className="mb-3">
                            <label className="small font-weight-bold">Contenido del Texto:</label>
                            <input type="text" className="form-control form-control-sm" value={customText} onChange={(e) => setCustomText(e.target.value)} placeholder="Ej: ¡Baila con nosotros!" />
                        </div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Escala:</span><span>{textScale}x</span></label><input type="range" className="form-range" min="0.3" max="2.5" step="0.1" value={textScale} onChange={(e) => setTextScale(parseFloat(e.target.value))} /></div>
                        <div className="mb-2"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span><span>{textX}px</span></label><input type="range" className="form-range" min="-800" max={MAX_X} step="10" value={textX} onChange={(e) => setTextX(parseInt(e.target.value))} /></div>
                        <div className="mb-3"><label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span><span>{textY}px</span></label><input type="range" className="form-range" min="-200" max={MAX_Y} step="10" value={textY} onChange={(e) => setTextY(parseInt(e.target.value))} /></div>

                        <div className="d-flex justify-content-center">
                            <div style={{ width: '100%', maxWidth: '250px', height: '140px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #dc3545', touchAction: 'none', cursor: isDraggingText ? 'grabbing' : 'grab' }} onPointerDown={handleTextPointerDown} onPointerMove={handleTextPointerMove} onPointerUp={handleTextPointerUp} onPointerCancel={handleTextPointerUp} onPointerLeave={handleTextPointerUp}>
                                <div style={{ position: 'absolute', right: `${(textX / MAX_X) * 80}%`, bottom: `${(textY / MAX_Y) * 80}%`, padding: '2px 8px', border: '1px solid #dc3545', backgroundColor: isDraggingText ? '#dc3545' : 'rgba(220, 53, 69, 0.5)', color: 'white', fontSize: '0.5rem', transform: `scale(${textScale})`, pointerEvents: 'none' }}>TEXTO</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        {/* ============================================================================================== */}
        {/* NUEVA SECCIÓN: CONTROLES DE LA KARAOKE CAM (OVERLAY) */}
        {/* ============================================================================================== */}
        {/* <div className="card shadow-sm border-info mt-5" style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div className="card-header bg-info text-white font-weight-bold d-flex justify-content-between align-items-center">
                <span><i className="bi bi-camera-video-fill me-2"></i> Karaoke Cam Overlay (OBS)</span>
                <span className={`badge ${isCamSyncing ? 'bg-warning text-dark' : 'bg-success'}`}>
                    {isCamSyncing ? 'Sincronizando...' : 'En Vivo'}
                </span>
            </div>
            <div className="card-body">
                
                {/* --- SWITCHES DE VISIBILIDAD (Arriba al centro) --- /}
                <div className="d-flex justify-content-center flex-wrap gap-4 mb-4 p-3 bg-light rounded border">
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showMic} onChange={(e) => setShowMic(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎤 Mostrar Micrófono</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🎵 Mostrar Notas</label>
                    </div>
                    <div className="form-check form-switch fs-5">
                        <input className="form-check-input" type="checkbox" checked={showText} onChange={(e) => setShowText(e.target.checked)} style={{cursor: 'pointer'}} />
                        <label className="form-check-label font-weight-bold ms-2">🔤 Mostrar Texto</label>
                    </div>
                    <button className="btn btn-outline-danger btn-sm font-weight-bold" onClick={() => {setShowMic(false); setShowNotes(false); setShowText(false);}}>Ocultar Todo</button>
                </div>

                <div className="row g-4">
                    {/* --- COLUMNA 1: CONTROLES DEL MICRÓFONO --- /}
                    <div className="col-md-6 border-end">
                        <h6 className="font-weight-bold text-primary border-bottom pb-2 mb-3 text-center">Física del Micrófono</h6>
                        
                        <div className="mb-3">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Rotación (Inclinación):</span> <span className="text-primary">{micRotation}°</span></label>
                            <input type="range" className="form-range" min="-90" max="90" step="5" value={micRotation} onChange={(e) => setMicRotation(parseInt(e.target.value))} />
                            <div className="d-flex justify-content-between small text-muted"><span>◀ Izquierda</span><span>Derecha ▶</span></div>
                        </div>

                        <div className="mb-2">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span> <span className="text-primary">{micX}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_X_MIC} step="10" value={micX} onChange={(e) => setMicX(parseInt(e.target.value))} />
                        </div>

                        <div className="mb-4">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span> <span className="text-primary">{micY}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_Y_MIC} step="10" value={micY} onChange={(e) => setMicY(parseInt(e.target.value))} />
                        </div>

                        {/* Trackpad del Micrófono /}
                        <div className="d-flex justify-content-center">
                            <div 
                                style={{ width: '100%', maxWidth: '300px', height: '168px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #0d6efd', touchAction: 'none', cursor: isDraggingMic ? 'grabbing' : 'grab' }}
                                onPointerDown={handleMicPointerDown} onPointerMove={handleMicPointerMove} onPointerUp={handleMicPointerUp} onPointerCancel={handleMicPointerUp} onPointerLeave={handleMicPointerUp}
                            >
                                <div style={{
                                    position: 'absolute', right: `${(micX / MAX_X_MIC) * 90}%`, bottom: `${(micY / MAX_Y_MIC) * 80}%`,
                                    width: '10%', height: '30%', backgroundColor: isDraggingMic ? '#0d6efd' : 'rgba(13, 110, 253, 0.5)', borderRadius: '10px',
                                    transform: `rotate(${micRotation}deg)`, transformOrigin: 'bottom center', pointerEvents: 'none', transition: isDraggingMic ? 'none' : 'all 0.1s ease-out'
                                }}></div>
                                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed #333', pointerEvents: 'none' }}></div>
                                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed #333', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                        <p className="text-center small text-muted mt-2">Trackpad Micrófono</p>
                    </div>

                    {/* --- COLUMNA 2: CONTROLES DE TEXTO/NOTAS --- /}
                    <div className="col-md-6">
                        <h6 className="font-weight-bold text-success border-bottom pb-2 mb-3 text-center">Física de Texto y Notas</h6>
                        
                        <div className="mb-2 mt-4 pt-3">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje X:</span> <span className="text-success">{textNotesX}px</span></label>
                            <input type="range" className="form-range" min="-500" max={MAX_X_TEXT} step="10" value={textNotesX} onChange={(e) => setTextNotesX(parseInt(e.target.value))} />
                        </div>

                        <div className="mb-4 pt-2">
                            <label className="d-flex justify-content-between small font-weight-bold"><span>Eje Y:</span> <span className="text-success">{textNotesY}px</span></label>
                            <input type="range" className="form-range" min="-200" max={MAX_Y_TEXT} step="10" value={textNotesY} onChange={(e) => setTextNotesY(parseInt(e.target.value))} />
                        </div>

                        {/* Trackpad de Texto/Notas /}
                        <div className="d-flex justify-content-center mt-5">
                            <div 
                                style={{ width: '100%', maxWidth: '300px', height: '168px', backgroundColor: '#1a1a1a', position: 'relative', overflow: 'hidden', borderRadius: '12px', border: '2px solid #198754', touchAction: 'none', cursor: isDraggingText ? 'grabbing' : 'grab' }}
                                onPointerDown={handleTextPointerDown} onPointerMove={handleTextPointerMove} onPointerUp={handleTextPointerUp} onPointerCancel={handleTextPointerUp} onPointerLeave={handleTextPointerUp}
                            >
                                <div style={{
                                    position: 'absolute', right: `${(textNotesX / MAX_X_TEXT) * 80}%`, bottom: `${(textNotesY / MAX_Y_TEXT) * 80}%`,
                                    padding: '5px 15px', border: '2px solid #198754', backgroundColor: isDraggingText ? '#198754' : 'rgba(25, 135, 84, 0.5)', borderRadius: '4px',
                                    color: 'white', fontSize: '0.6rem', fontWeight: 'bold', pointerEvents: 'none', transition: isDraggingText ? 'none' : 'all 0.1s ease-out'
                                }}>
                                    TEXTO / NOTAS
                                </div>
                                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, borderTop: '1px dashed #333', pointerEvents: 'none' }}></div>
                                <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, borderLeft: '1px dashed #333', pointerEvents: 'none' }}></div>
                            </div>
                        </div>
                        <p className="text-center small text-muted mt-2">Trackpad Texto y Notas</p>

                    </div>
                </div>
            </div>
        </div> */}

        {/* Modal Colores */}
        <ColorSettingsModal 
          isOpen={isColorModalOpen}
          onClose={() => setIsColorModalOpen(false)}
          currentSettings={colorSettings}
          onSave={handleSaveColors}
          onReset={handleResetColors}
        />

      </div>
    </SecondaryBase>
  );
};

export { Karaoke };










// Funcional
// import React, { useState, useEffect, useRef, useMemo } from 'react'
// import { useNavigate } from 'react-router-dom'

// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
// import { SecondaryBase } from './../Commons/SecondaryBase'

// import { LyricsGenerator } from './Karaoke/LyricsGenerator'
// import { KaraokeSongCreator } from './Karaoke/KaraokeSongCreator'

// import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'
// import { ColorSettingsModal } from './Karaoke/ColorSettingsModal'


// /**
//  * ==============================================================================
//  * HELPERS (Cálculos de tiempo)
//  * ==============================================================================
//  */
// const getBlockDurationMs = (block) => {
//   if (!block) return 0;
//   if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0);
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
// };

// const getBlockDisplayText = (block) => {
//   if (!block) return "";
//   if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : "";
//   if (block.mode === 'uniform') return block.text || "";
//   return (block.parts || []).map(p => p.text || "").join("");
// };

// // Valores por defecto para colores
// const DEFAULT_COLORS = { base: '#ffffff', fill: '#ffd400', stroke: '#000000' };

// /**
//  * ==============================================================================
//  * COMPONENTE: KARAOKE DASHBOARD
//  * ==============================================================================
//  */
// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate();
//   const { showMessage } = useFloatMessage();

//   // --------------------------------------------------------------------------------
//   // 1. ESTADOS DE DATOS
//   // --------------------------------------------------------------------------------
//   const [songs, setSongs] = useState([]);               
//   const [selectedSongId, setSelectedSongId] = useState(""); 

//   // --------------------------------------------------------------------------------
//   // 2. ESTADOS DEL REPRODUCTOR
//   // --------------------------------------------------------------------------------
//   const [isPlaying, setIsPlaying] = useState(false);        
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0); 
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0); 

//   // --------------------------------------------------------------------------------
//   // 3. ESTADOS DE CONFIGURACIÓN Y UI
//   // --------------------------------------------------------------------------------
//   const [isColorModalOpen, setIsColorModalOpen] = useState(false); 
  
//   // Estado de los botones toggle
//   const [isFillingEnabled, setIsFillingEnabled] = useState(true);
//   const [isVisible, setIsVisible] = useState(true);

//   const [colorSettings, setColorSettings] = useState(DEFAULT_COLORS);

//   // --------------------------------------------------------------------------------
//   // 4. REFERENCIAS
//   // --------------------------------------------------------------------------------
//   const rafIdRef = useRef(null);       
//   const lastFrameTimeRef = useRef(0);  
//   const lyricsListRef = useRef(null);  

//   // --------------------------------------------------------------------------------
//   // 5. COMPUTADOS
//   // --------------------------------------------------------------------------------
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
//   const activeBlock = blocks[activeBlockIndex] || null;
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);

//   // Progreso visual local (Preview)
//   const fillProgress = useMemo(() => {
//     // Si el rellenado está desactivado visualmente, forzamos 0 o 100 según lógica, 
//     // pero para el preview local seguiremos mostrando el progreso si se desea, 
//     // o lo ocultamos igual que en pantalla. Aquí respetamos el botón:
//     if (!isFillingEnabled) return 0; 

//     const total = getBlockDurationMs(activeBlock);
//     if (total <= 0) return 0;
//     if (activeBlock?.type === 'instrumental') return 0; 
//     return Math.min(Math.max(elapsedInBlockMs, 0), total) / total;
//   }, [activeBlock, elapsedInBlockMs, isFillingEnabled]);

//   // Leyenda de estatus
//   const statusLegend = useMemo(() => {
//     if (!selectedSongId) return "Sin canción seleccionada";
//     if (isPlaying) return `En reproducción - Línea #${activeBlockIndex + 1}`;
//     if (activeBlockIndex > 0 && activeBlockIndex < blocks.length) return `Pausado - Línea #${activeBlockIndex + 1}`;
//     if (activeBlockIndex === 0 && !isPlaying) return "Detenido (Inicio)";
//     return "Listo";
//   }, [selectedSongId, isPlaying, activeBlockIndex, blocks.length]);

//   // --------------------------------------------------------------------------------
//   // 6. CICLO DE VIDA: CARGA INICIAL
//   // --------------------------------------------------------------------------------
  
//   useEffect(() => {
//     const loadSongs = async () => {
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`);
//             if (response.ok) {
//                 const data = await response.json();
//                 setSongs(data.songs || []);
//             }
//         } catch (error) { console.error("Error cargando canciones", error); }
//     };
//     loadSongs();
//   }, []);

//   // Resetear player al cambiar canción
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   // --------------------------------------------------------------------------------
//   // 7. COMUNICACIÓN API (CONTROL DE PANTALLA)
//   // --------------------------------------------------------------------------------

//   // Toggle Ocultar/Mostrar
//   const handleToggleVisible = async () => {
//     const newValue = !isVisible;
//     setIsVisible(newValue); 
//     try { 
//         await fetch(`${process.env.REACT_APP_API_URL}karaoke/visible?visible=${newValue}`, { method: 'POST' }); 
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Letras mostradas en pantalla' : 'Letras ocultas' });
//     } catch (e) { 
//         console.error(e);
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de conexión al cambiar visibilidad' });
//     }
//   };

//   // Toggle Rellenar
//   const handleToggleFilling = async () => {
//     const newValue = !isFillingEnabled;
//     setIsFillingEnabled(newValue); 
//     try { 
//         // 1. Enviamos el cambio de configuración global
//         await fetch(`${process.env.REACT_APP_API_URL}karaoke/fill?fill=${newValue}`, { method: 'POST' }); 
        
//         // 2. TRUCO: Re-enviamos la línea actual inmediatamente para forzar el cambio visual "en vivo"
//         // Si desactivamos el relleno, esto hará que la pantalla reciba la instrucción de cortar la animación.
//         if (activeBlock) {
//              sendCurrentLyricToScreen(activeBlock, newValue); // Pasamos el nuevo valor explícito
//         }

//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: newValue ? 'Rellenado activado' : 'Rellenado desactivado' });
//     } catch (e) { 
//         console.error(e); 
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al cambiar configuración de relleno' });
//     }
//   };

//   // Guardar Colores
//   const handleSaveColors = async (newColors) => {
//     setColorSettings(newColors);
//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/colors`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ BaseColor: newColors.base, FillColor: newColors.fill, StrokeColor: newColors.stroke })
//       });
//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Colores actualizados correctamente' });
//     } catch (e) { 
//         console.error(e);
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error al guardar colores' });
//     }
//   };

//   // Resetear Colores a Default
//   const handleResetColors = () => {
//       // Llamamos a guardar con los defaults
//       handleSaveColors(DEFAULT_COLORS);
//       // El modal se cerrará automáticamente o actualizará su estado visual al recibir props nuevas si está diseñado así,
//       // pero para asegurar feedback inmediato en el modal (si sigue abierto):
//       setColorSettings(DEFAULT_COLORS); 
//   };

//   // Eliminar Canción
//   const handleDeleteSong = async () => {
//     if (!selectedSongId) return;
//     if (!window.confirm("¿Seguro que deseas eliminar esta canción?")) return;

//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs/${selectedSongId}`, {
//         method: 'DELETE'
//       });

//       if (response.ok) {
//         setSongs(prev => prev.filter(s => s.id !== selectedSongId));
//         setSelectedSongId(""); 
//         stopPlayback(); 
//         showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Canción eliminada correctamente' });
//       } else {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: 'No se pudo eliminar la canción' });
//       }
//     } catch (error) {
//       console.error(error);
//       showMessage({ type: MESSAGE_TYPE.BAD, text: 'Error de red al eliminar' });
//     }
//   };

//   // Enviar Línea a Pantalla
//   // Nota: Agregué parámetro opcional 'forceFilling' para el caso del botón toggle
//   const sendCurrentLyricToScreen = async (block, forceFilling = null) => {
//     if (!block) return;
    
//     // Si forceFilling no es null, usamos ese valor, si no, usamos el estado actual
//     // Esto es útil porque el estado 'isFillingEnabled' puede tardar un ciclo en actualizarse dentro de esta función
//     // si se llama inmediatamente después del set. Pero para el flujo normal del player, el estado está bien.
    
//     const payload = {
//       text: block.mode === 'uniform' ? block.text : block.parts?.map(p => p.text).join("") || "",
//       mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
//       duration: getBlockDurationMs(block)
//     };

//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/lyric`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload)
//       });
//     } catch (e) { console.error("Error enviando lyric:", e); }
//   };

//   // Sincronización de Bloque (Efecto automático al cambiar línea)
//   useEffect(() => {
//     if (activeBlock) {
//       sendCurrentLyricToScreen(activeBlock);
//       scrollToActiveLyricInList(); 
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [activeBlockIndex]);

//   // --------------------------------------------------------------------------------
//   // 8. FUNCIONES DE LISTA (SCROLL)
//   // --------------------------------------------------------------------------------

//   const scrollToActiveLyricInList = () => {
//     if (lyricsListRef.current) {
//       const listItems = lyricsListRef.current.children;
//       const activeItem = listItems[activeBlockIndex];
//       if (activeItem) {
//         activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       }
//     }
//   };

//   const scrollListUp = () => {
//       if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: -100, behavior: 'smooth' });
//   };

//   const scrollListDown = () => {
//       if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: 100, behavior: 'smooth' });
//   };

//   // --------------------------------------------------------------------------------
//   // 9. CONTROLES DE REPRODUCCIÓN
//   // --------------------------------------------------------------------------------

//   const playPlayback = () => {
//     if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona una canción válida.' });
    
//     setIsPlaying(true);

//     // --- NUEVO: Forzar envío inmediato al dar Play ---
//     // Esto hace que se proyecte la línea actual sin esperar a que avance a la siguiente
//     if (activeBlock) {
//         sendCurrentLyricToScreen(activeBlock);
//     }
//     // ------------------------------------------------

//     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reproduciendo...' });
//   };

//   const pausePlayback = () => {
//     setIsPlaying(false);
//     lastFrameTimeRef.current = 0;
//     showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Pausado' });
//   };

//   const stopPlayback = () => {
//     setIsPlaying(false);
//     setActiveBlockIndex(0);
//     setElapsedInBlockMs(0);
//     lastFrameTimeRef.current = 0;
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//     // showMessage({ type: MESSAGE_TYPE.INFO, text: 'Detenido / Inicio' }); // Opcional, puede ser molesto si sale mucho
//   };

//   const goToNext = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
//   };

//   const goToPrev = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
//   };
  
//   const goToStart = () => { 
//       stopPlayback(); 
//       showMessage({ type: MESSAGE_TYPE.INFO, text: 'Regresando al inicio' });
//   };
  
//   const goToEnd = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(blocks.length - 1);
//     showMessage({ type: MESSAGE_TYPE.INFO, text: 'Saltando al final' });
//   };

//   // Función específica para el click en la lista (Lyrics Right Panel)
//   const handleJumpToLine = (index) => {
//       setElapsedInBlockMs(0);
//       setActiveBlockIndex(index);
//       // Opcional: Si quieres que al dar click empiece a reproducir automáticamente:
//       // setIsPlaying(true); 
//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Saltando a línea #${index + 1}` });
//   };

//   // --------------------------------------------------------------------------------
//   // 10. LOOP DE ANIMACIÓN
//   // --------------------------------------------------------------------------------
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const deltaMs = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock);
//         const nextElapsed = prev + deltaMs;

//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1;
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false); 
//             return duration; 
//           }
//           setActiveBlockIndex(nextIdx); 
//           lastFrameTimeRef.current = 0; 
//           return 0;
//         }
//         return nextElapsed;
//       });
//       rafIdRef.current = requestAnimationFrame(tick);
//     };
//     rafIdRef.current = requestAnimationFrame(tick);
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);

//   const ReturnToDashboard = async () => {
//     try {
//       await BLResetCurrentDynamic(); // Asumo que esto está importado o disponible
//       getOutOfTheDynamic();
//     } catch (error) { }
//   }

//   // --------------------------------------------------------------------------------
//   // 11. RENDERIZADO
//   // --------------------------------------------------------------------------------
//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//       <div className='Karaoke'>
        
//         <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

//         <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
//           <i className='bi bi-plus-circle'></i> Crear canción
//         </button>

//         <div className='Karaoke__Container'>
          
//           {/* === PANEL DECK === */}
//           <div className='Karaoke__Container__Deck Element--Shadow'>
            
//             {/* A. Botones de Configuración */}
//             <div className='Karaoke__Container__Deck__SettingButtons'>
//               {selectedSongId && (
//                 <button 
//                     type="button" 
//                     className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'
//                     onClick={handleDeleteSong}
//                     title="Borrar canción"
//                 >
//                   <i className='bi bi-trash'></i> Borrar canción
//                 </button>
//               )}

//               {/* Botón Rellenar */}
//               <button 
//                 type='button' 
//                 className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isFillingEnabled ? 'Btn__Adsl--Red' : ''}`} 
//                 onClick={handleToggleFilling}
//                 title={isFillingEnabled ? "Desactivar Relleno" : "Activar Relleno"}
//               >
//                 <i className='bi bi-paint-bucket'></i> {isFillingEnabled ? 'Sin relleno' : 'Rellenar'}
//               </button>
              
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => setIsColorModalOpen(true)}>
//                 <i className='bi bi-palette'></i> Colores
//               </button>
              
//               {/* Botón Ocultar/Mostrar */}
//               <button 
//                 type='button' 
//                 className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isVisible ? 'Btn__Adsl--Red' : ''}`} 
//                 onClick={handleToggleVisible}
//                 title={isVisible ? "Ocultar en Pantalla" : "Mostrar en Pantalla"}
//               >
//                 <i className={`bi ${isVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i> {isVisible ? 'Ocultar' : 'Mostrar'}
//               </button>
//             </div>

//             {/* B. Playlist + Leyenda */}
//             <div style={{ marginBottom: 10 }}>
//                 <div style={{ display: 'flex', gap: 8 }}>
//                     <select 
//                         className='Karaoke__Container__Deck__Playlist' 
//                         value={selectedSongId} 
//                         onChange={(e) => setSelectedSongId(e.target.value)}
//                         style={{ flex: 1 }}
//                     >
//                         <option value="">-- Selecciona una canción --</option>
//                         {songs.map(song => (
//                             <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
//                         ))}
//                     </select>
//                 </div>
//                 <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#666", marginTop: 5, fontWeight: "bold" }}>
//                     {statusLegend}
//                 </div>
//             </div>

//             {/* C. Preview */}
//             <div className='Karaoke__Container__Deck__Screen'>
//                <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
//                  <div style={{ position: "relative", textAlign: "center", fontSize: 24, fontWeight: "900" }}>
//                     <div style={{ color: colorSettings.base, WebkitTextStroke: `1px ${colorSettings.stroke}` }}>
//                         {displayText || "..."}
//                     </div>
//                     {/* Visualización local del relleno: obedece al estado isFillingEnabled */}
//                     {isFillingEnabled && (
//                         <div style={{ 
//                             position: "absolute", inset: 0, overflow: "hidden", whiteSpace: "nowrap",
//                             width: `${(fillProgress * 100).toFixed(2)}%`,
//                             color: colorSettings.fill, 
//                             WebkitTextStroke: `1px ${colorSettings.stroke}`
//                         }}>
//                             {displayText || ""}
//                         </div>
//                     )}
//                  </div>
//                </div>
//             </div>

//             {/* D. Controles Playback */}
//             <div className='Karaoke__Container__Deck__ControlButtons'>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Línea Anterior"><i className='bi bi-arrow-left'></i></button>
              
//               {!isPlaying ? (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
//               ) : (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
//               )}

//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Línea Siguiente"><i className='bi bi-arrow-right'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
//             </div>
//           </div>

//           {/* === PANEL LYRICS === */}
//           <div className='Karaoke__Container__Lyrics Element--Shadow'>
//             {!selectedSong ? (
//                <p style={{textAlign:"center", color:"#999", marginTop: 20}}>Selecciona una canción.</p>
//             ) : (
//                <>
//                  <div style={{textAlign:"center", marginBottom: 5, fontWeight:"bold", fontSize:"0.8rem"}}>
//                     Total Líneas: {blocks.length}
//                  </div>

//                  {/* Botón Scroll Arriba */}
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListUp}>
//                     <i className='bi bi-chevron-up'></i>
//                  </button>

//                  <div className='Karaoke__Container__Lyrics__Box'>
//                    <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
//                      {blocks.map((block, index) => {
//                        const text = block.type === 'instrumental' 
//                           ? '🎵 (Instrumental)' 
//                           : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""));
                       
//                        const isActive = index === activeBlockIndex;

//                        return (
//                          <li 
//                            key={block.id || index} 
//                            className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
//                            onClick={() => handleJumpToLine(index)}
//                          >
//                            <strong>#{index + 1}</strong> - {text}
//                          </li>
//                        );
//                      })}
//                    </ul>
//                  </div>

//                  {/* Botón Scroll Abajo */}
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListDown}>
//                     <i className='bi bi-chevron-down'></i>
//                  </button>
//                </>
//             )}
//           </div>
//         </div>

//         {/* Modal Colores */}
//         <ColorSettingsModal 
//           isOpen={isColorModalOpen}
//           onClose={() => setIsColorModalOpen(false)}
//           currentSettings={colorSettings}
//           onSave={handleSaveColors}
//           onReset={handleResetColors}
//         />

//       </div>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };
























// /**
//  * ==============================================================================
//  * HELPERS (Cálculos de tiempo)
//  * ==============================================================================
//  */
// const getBlockDurationMs = (block) => {
//   if (!block) return 0;
//   if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0);
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
// };

// const getBlockDisplayText = (block) => {
//   if (!block) return "";
//   if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : "";
//   if (block.mode === 'uniform') return block.text || "";
//   return (block.parts || []).map(p => p.text || "").join("");
// };

// // Valores por defecto para colores (para el botón de reset)
// const DEFAULT_COLORS = { base: '#ffffff', fill: '#ffd400', stroke: '#000000' };

// /**
//  * ==============================================================================
//  * COMPONENTE: KARAOKE DASHBOARD
//  * ==============================================================================
//  */
// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate();
//   // const { showMessage } = useFloatMessage(); // Descomenta si lo usas

//   // --------------------------------------------------------------------------------
//   // 1. ESTADOS DE DATOS
//   // --------------------------------------------------------------------------------
//   const [songs, setSongs] = useState([]);               
//   const [selectedSongId, setSelectedSongId] = useState(""); 

//   // --------------------------------------------------------------------------------
//   // 2. ESTADOS DEL REPRODUCTOR
//   // --------------------------------------------------------------------------------
//   const [isPlaying, setIsPlaying] = useState(false);        
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0); 
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0); 

//   // --------------------------------------------------------------------------------
//   // 3. ESTADOS DE CONFIGURACIÓN Y UI (Toggle buttons)
//   // --------------------------------------------------------------------------------
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false);     
//   const [isColorModalOpen, setIsColorModalOpen] = useState(false); 
  
//   // Estado de los botones toggle (se inicializan en true, o deberían venir de BD)
//   const [isFillingEnabled, setIsFillingEnabled] = useState(true);
//   const [isVisible, setIsVisible] = useState(true);

//   const [colorSettings, setColorSettings] = useState(DEFAULT_COLORS);

//   // --------------------------------------------------------------------------------
//   // 4. REFERENCIAS
//   // --------------------------------------------------------------------------------
//   const rafIdRef = useRef(null);       
//   const lastFrameTimeRef = useRef(0);  
//   const lyricsListRef = useRef(null);  

//   // --------------------------------------------------------------------------------
//   // 5. COMPUTADOS
//   // --------------------------------------------------------------------------------
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
//   const activeBlock = blocks[activeBlockIndex] || null;
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);

//   // Progreso visual local
//   const fillProgress = useMemo(() => {
//     const total = getBlockDurationMs(activeBlock);
//     if (total <= 0) return 0;
//     if (activeBlock?.type === 'instrumental') return 0; 
//     return Math.min(Math.max(elapsedInBlockMs, 0), total) / total;
//   }, [activeBlock, elapsedInBlockMs]);

//   // Generar leyenda de estatus (Punto 2 Deck)
//   const statusLegend = useMemo(() => {
//     if (!selectedSongId) return "Sin canción seleccionada";
//     if (isPlaying) return `En reproducción - Línea #${activeBlockIndex + 1}`;
//     if (activeBlockIndex > 0 && activeBlockIndex < blocks.length) return `Pausado - Línea #${activeBlockIndex + 1}`;
//     if (activeBlockIndex === 0 && !isPlaying) return "Detenido (Inicio)";
//     return "Listo";
//   }, [selectedSongId, isPlaying, activeBlockIndex, blocks.length]);

//   // --------------------------------------------------------------------------------
//   // 6. CICLO DE VIDA: CARGA Y PERSISTENCIA
//   // --------------------------------------------------------------------------------
  
//   useEffect(() => {
//     const init = async () => {
//         // A) Cargar Canciones
//         try {
//             const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`);
//             if (response.ok) {
//                 const data = await response.json();
//                 setSongs(data.songs || []);
//             }
//         } catch (error) { console.error("Error cargando canciones", error); }

//         // B) Precargar estado (Opciones y última línea) - Punto 1 Deck
//         // Aquí deberías llamar a un endpoint real si guardas el estado en BD (ej. /karaoke/state)
//         // Por ahora simulamos que leemos los defaults o valores persistidos
//         /*
//         try {
//             const stateResponse = await fetch(`${process.env.REACT_APP_API_URL}karaoke/state`);
//             if (stateResponse.ok) {
//                 const state = await stateResponse.json();
//                 if(state.selectedSongId) setSelectedSongId(state.selectedSongId);
//                 if(state.lastLineIndex) setActiveBlockIndex(state.lastLineIndex);
//                 setIsFillingEnabled(state.isFilling ?? true);
//                 setIsVisible(state.isVisible ?? true);
//                 if(state.colors) setColorSettings(state.colors);
//             }
//         } catch (e) {}
//         */
//     };
//     init();
//   }, []);

//   // Resetear player al cambiar canción
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   // --------------------------------------------------------------------------------
//   // 7. COMUNICACIÓN API (CONTROL DE PANTALLA)
//   // --------------------------------------------------------------------------------

//   // Toggle Ocultar/Mostrar (Punto 5 Deck)
//   const handleToggleVisible = async () => {
//     const newValue = !isVisible;
//     setIsVisible(newValue); // Actualizamos visualmente el botón
//     try { 
//         await fetch(`${process.env.REACT_APP_API_URL}karaoke/visible?visible=${newValue}`, { method: 'POST' }); 
//     } catch (e) { console.error(e); }
//   };

//   // Toggle Rellenar (Punto 3 Deck)
//   const handleToggleFilling = async () => {
//     const newValue = !isFillingEnabled;
//     setIsFillingEnabled(newValue); // Actualizamos visualmente el botón
//     try { 
//         await fetch(`${process.env.REACT_APP_API_URL}karaoke/fill?fill=${newValue}`, { method: 'POST' }); 
//     } catch (e) { console.error(e); }
//   };

//   // Guardar Colores
//   const handleSaveColors = async (newColors) => {
//     setColorSettings(newColors);
//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/colors`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ BaseColor: newColors.base, FillColor: newColors.fill, StrokeColor: newColors.stroke })
//       });
//     } catch (e) { console.error(e); }
//   };

//   // Resetear Colores a Default (Punto 4 Deck - Lógica)
//   const handleResetColors = async () => {
//       handleSaveColors(DEFAULT_COLORS);
//       // Opcional: Cerrar modal si se desea, o dejarlo abierto para ver el cambio
//   };

//   // Función para eliminar
//   const handleDeleteSong = async () => {
//     if (!selectedSongId) return;
    
//     // Confirmación simple
//     if (!window.confirm("¿Seguro que deseas eliminar esta canción?")) return;

//     try {
//       const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs/${selectedSongId}`, {
//         method: 'DELETE'
//       });

//       if (response.ok) {
//         // Quitamos la canción de la lista local
//         setSongs(prev => prev.filter(s => s.id !== selectedSongId));
//         setSelectedSongId(""); // Reseteamos selección
//         stopPlayback(); // Detenemos si estaba sonando
//       } else {
//         alert("Error al eliminar");
//       }
//     } catch (error) {
//       console.error("Error de red:", error);
//     }
//   };

//   // Enviar Línea a Pantalla
//   const sendCurrentLyricToScreen = async (block) => {
//     if (!block) return;
//     const payload = {
//       text: block.mode === 'uniform' ? block.text : block.parts?.map(p => p.text).join("") || "",
//       mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
//       duration: getBlockDurationMs(block)
//     };

//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/lyric`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload)
//       });
      
//       // AQUÍ: Podrías guardar en BD la línea actual para persistencia "ActiveBlockIndex"
//       // await fetch(..., { body: { lastLineIndex: activeBlockIndex } })
//     } catch (e) { console.error(e); }
//   };

//   // Sincronización de Bloque
//   useEffect(() => {
//     if (activeBlock) {
//       sendCurrentLyricToScreen(activeBlock);
//       scrollToActiveLyricInList(); // Auto-scroll tipo candado
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [activeBlockIndex]);

//   // --------------------------------------------------------------------------------
//   // 8. FUNCIONES DE LISTA (SCROLL)
//   // --------------------------------------------------------------------------------

//   // Scroll automático para centrar la línea activa ("Candado")
//   const scrollToActiveLyricInList = () => {
//     if (lyricsListRef.current) {
//       const listItems = lyricsListRef.current.children;
//       const activeItem = listItems[activeBlockIndex];
//       if (activeItem) {
//         activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       }
//     }
//   };

//   // Desplazamiento manual de la lista (Punto 1 Lyrics)
//   // No afecta la reproducción, solo mueve el scroll visualmente
//   const scrollListUp = () => {
//       if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: -100, behavior: 'smooth' });
//   };

//   const scrollListDown = () => {
//       if (lyricsListRef.current) lyricsListRef.current.scrollBy({ top: 100, behavior: 'smooth' });
//   };

//   // --------------------------------------------------------------------------------
//   // 9. CONTROLES DE REPRODUCCIÓN
//   // --------------------------------------------------------------------------------

//   const playPlayback = () => {
//     if (!blocks.length) return; // showMessage(...)
//     setIsPlaying(true);
//   };

//   const pausePlayback = () => {
//     setIsPlaying(false);
//     lastFrameTimeRef.current = 0;
//   };

//   const stopPlayback = () => {
//     setIsPlaying(false);
//     setActiveBlockIndex(0);
//     setElapsedInBlockMs(0);
//     lastFrameTimeRef.current = 0;
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//   };

//   const goToNext = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
//   };

//   const goToStart = () => {
//     // Simplemente detenemos todo, lo cual ya resetea el índice a 0 en la función stopPlayback
//     stopPlayback(); 
//   };

//   const goToPrev = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
//   };
  
//   const goToEnd = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(blocks.length - 1);
//   };

//   // --------------------------------------------------------------------------------
//   // 10. LOOP DE ANIMACIÓN
//   // --------------------------------------------------------------------------------
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const deltaMs = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock);
//         const nextElapsed = prev + deltaMs;

//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1;
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false);
//             return duration; 
//           }
//           setActiveBlockIndex(nextIdx); 
//           lastFrameTimeRef.current = 0; 
//           return 0;
//         }
//         return nextElapsed;
//       });
//       rafIdRef.current = requestAnimationFrame(tick);
//     };
//     rafIdRef.current = requestAnimationFrame(tick);
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);

//   const ReturnToDashboard = async () => {
//     try {
//       await BLResetCurrentDynamic();
//       getOutOfTheDynamic();
//     } catch (error) { }
//   }

//   // --------------------------------------------------------------------------------
//   // 11. RENDERIZADO
//   // --------------------------------------------------------------------------------
//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//       <div className='Karaoke'>
        
//         <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

//         <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
//           <i className='bi bi-plus-circle'></i> Crear canción
//         </button>

//         <div className='Karaoke__Container'>
          
//           {/* === PANEL DECK === */}
//           <div className='Karaoke__Container__Deck Element--Shadow'>
            
//             {/* A. Botones de Configuración */}
//             <div className='Karaoke__Container__Deck__SettingButtons'>
//               {/* Botón Rellenar (Toggle visual) */}
//               <button 
//                 type='button' 
//                 className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isFillingEnabled ? 'Btn__Adsl--Red' : ''}`} 
//                 onClick={handleToggleFilling}
//                 title={isFillingEnabled ? "Desactivar Relleno" : "Activar Relleno"}
//               >
//                 <i className='bi bi-paint-bucket'></i> Rellenar
//               </button>
              
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => setIsColorModalOpen(true)}>
//                 <i className='bi bi-palette'></i> Colores
//               </button>
              
//               {/* Botón Ocultar/Mostrar (Toggle visual y de ícono) */}
//               <button 
//                 type='button' 
//                 className={`Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons ${isVisible ? 'Btn__Adsl--Red' : ''}`} 
//                 onClick={handleToggleVisible}
//                 title={isVisible ? "Ocultar en Pantalla" : "Mostrar en Pantalla"}
//               >
//                 <i className={`bi ${isVisible ? 'bi-eye' : 'bi-eye-slash'}`}></i> {isVisible ? 'Mostrar' : 'Ocultar'}
//               </button>
//             </div>

//             {/* B. Playlist + Leyenda de Estatus */}
//             <div style={{ marginBottom: 10 }}>
//                 <select className='Karaoke__Container__Deck__Playlist' value={selectedSongId} onChange={(e) => setSelectedSongId(e.target.value)}>
//                   <option value="">-- Selecciona una canción --</option>
//                   {songs.map(song => (
//                       <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
//                   ))}
//                 </select>

//                 {/* Botón Eliminar (Solo aparece si hay canción seleccionada) */}
//                 {selectedSongId && (
//                     <button 
//                         type="button" 
//                         className='Btn__Adsl--RedOutlineTransparent'
//                         onClick={handleDeleteSong}
//                         title="Eliminar Canción"
//                         style={{ padding: '0 12px' }}
//                     >
//                         <i className='bi bi-trash'></i>
//                     </button>
//                 )}

//                 {/* Leyenda de estatus */}
//                 <div style={{ textAlign: "center", fontSize: "0.85rem", color: "#666", marginTop: 5, fontWeight: "bold" }}>
//                     {statusLegend}
//                 </div>
//             </div>

//             {/* C. Preview */}
//             <div className='Karaoke__Container__Deck__Screen'>
//                <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
//                  <div style={{ position: "relative", textAlign: "center", fontSize: 24, fontWeight: "900" }}>
//                     <div style={{ color: colorSettings.base, WebkitTextStroke: `1px ${colorSettings.stroke}` }}>
//                         {displayText || "..."}
//                     </div>
//                     {isFillingEnabled && (
//                         <div style={{ 
//                             position: "absolute", inset: 0, overflow: "hidden", whiteSpace: "nowrap",
//                             width: `${(fillProgress * 100).toFixed(2)}%`,
//                             color: colorSettings.fill, 
//                             WebkitTextStroke: `1px ${colorSettings.stroke}`
//                         }}>
//                             {displayText || ""}
//                         </div>
//                     )}
//                  </div>
//                </div>
//             </div>

//             {/* D. Controles Playback */}
//             <div className='Karaoke__Container__Deck__ControlButtons'>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Línea Anterior"><i className='bi bi-arrow-left'></i></button>
              
//               {!isPlaying ? (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
//               ) : (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
//               )}

//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Línea Siguiente"><i className='bi bi-arrow-right'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
//             </div>
//           </div>

//           {/* === PANEL LYRICS === */}
//           <div className='Karaoke__Container__Lyrics Element--Shadow'>
//             {!selectedSong ? (
//                <p style={{textAlign:"center", color:"#999", marginTop: 20}}>Selecciona una canción.</p>
//             ) : (
//                <>
//                  <div style={{textAlign:"center", marginBottom: 5, fontWeight:"bold", fontSize:"0.8rem"}}>
//                     Total Líneas: {blocks.length}
//                  </div>

//                  {/* Botón Scroll Arriba (Solo visual) */}
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListUp}>
//                     <i className='bi bi-chevron-up'></i>
//                  </button>

//                  <div className='Karaoke__Container__Lyrics__Box'>
//                    <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
//                      {blocks.map((block, index) => {
//                        const text = block.type === 'instrumental' 
//                           ? '🎵 (Instrumental)' 
//                           : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""));
                       
//                        const isActive = index === activeBlockIndex;

//                        return (
//                          <li 
//                            key={block.id || index} 
//                            className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
//                            onClick={() => {
//                              setElapsedInBlockMs(0);
//                              setActiveBlockIndex(index);
//                            }}
//                          >
//                            {/* Número de línea agregado */}
//                            <strong>#{index + 1}</strong> - {text}
//                          </li>
//                        );
//                      })}
//                    </ul>
//                  </div>

//                  {/* Botón Scroll Abajo (Solo visual) */}
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={scrollListDown}>
//                     <i className='bi bi-chevron-down'></i>
//                  </button>
//                </>
//             )}
//           </div>
//         </div>

//         {/* Modal Colores */}
//         <ColorSettingsModal 
//           isOpen={isColorModalOpen}
//           onClose={() => setIsColorModalOpen(false)}
//           currentSettings={colorSettings}
//           onSave={handleSaveColors}
//           onReset={handleResetColors} // Nueva prop para el botón de default
//         />

//       </div>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };




































// /**
//  * ==============================================================================
//  * HELPERS (Funciones auxiliares para cálculos de tiempo)
//  * ==============================================================================
//  */

// // Calcula la duración total de un bloque (sea Uniforme, Instrumental o por Partes)
// const getBlockDurationMs = (block) => {
//   if (!block) return 0;
//   // Si es instrumental o línea uniforme, tomamos durationMs directo
//   if (block.type === 'instrumental' || block.mode === 'uniform') return Math.max(0, Number(block.durationMs) || 0);
//   // Si es por partes, sumamos la duración de cada sílaba
//   return (block.parts || []).reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
// };

// // Genera el texto plano a mostrar (concatena partes si es necesario)
// const getBlockDisplayText = (block) => {
//   if (!block) return "";
//   if (block.type === 'instrumental') return block.display === 'icon' ? "♪" : "";
//   if (block.mode === 'uniform') return block.text || "";
//   return (block.parts || []).map(p => p.text || "").join("");
// };

// /**
//  * ==============================================================================
//  * COMPONENTE PRINCIPAL: KARAOKE DASHBOARD
//  * ------------------------------------------------------------------------------
//  * Módulo administrativo para controlar la reproducción de Karaoke en la Pantalla.
//  * ==============================================================================
//  */
// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate();
//   const { showMessage } = useFloatMessage()

//   // --------------------------------------------------------------------------------
//   // 1. ESTADOS DE DATOS (Canciones y Selección)
//   // --------------------------------------------------------------------------------
//   const [songs, setSongs] = useState([]);               // Lista cargada de la BD
//   const [selectedSongId, setSelectedSongId] = useState(""); // ID de la canción actual

//   // --------------------------------------------------------------------------------
//   // 2. ESTADOS DEL REPRODUCTOR (Lógica de tiempo y avance)
//   // --------------------------------------------------------------------------------
//   const [isPlaying, setIsPlaying] = useState(false);        // ¿Está corriendo el loop?
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0); // Índice del bloque actual
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0); // Tiempo transcurrido en EL bloque actual

//   // --------------------------------------------------------------------------------
//   // 3. ESTADOS DE UI Y CONFIGURACIÓN (Modales y Colores)
//   // --------------------------------------------------------------------------------
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false);     // Modal Creador
//   const [isColorModalOpen, setIsColorModalOpen] = useState(false); // Modal Colores
  
//   // Configuración visual (se envía a pantalla y se usa en el preview local)
//   const [colorSettings, setColorSettings] = useState({
//     base: '#ffffff',   // Texto inactivo
//     fill: '#ffd400',   // Texto cantado (relleno)
//     stroke: '#000000'  // Borde
//   });

//   // --------------------------------------------------------------------------------
//   // 4. REFERENCIAS (Para animación y scroll)
//   // --------------------------------------------------------------------------------
//   const rafIdRef = useRef(null);       // ID del requestAnimationFrame (para poder cancelarlo)
//   const lastFrameTimeRef = useRef(0);  // Marca de tiempo del último frame (para calcular delta)
//   const lyricsListRef = useRef(null);  // Referencia al contenedor de la lista (<ul>) para auto-scroll

//   // --------------------------------------------------------------------------------
//   // 5. VALORES COMPUTADOS (Optimizados con useMemo)
//   // --------------------------------------------------------------------------------
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
//   const activeBlock = blocks[activeBlockIndex] || null;
//   const displayText = useMemo(() => getBlockDisplayText(activeBlock), [activeBlock]);

//   // Calcula el porcentaje de relleno (0.0 a 1.0) para el Preview local
//   const fillProgress = useMemo(() => {
//     const total = getBlockDurationMs(activeBlock);
//     if (total <= 0) return 0;
//     if (activeBlock?.type === 'instrumental') return 0; // Instrumental no se rellena visualmente
//     return Math.min(Math.max(elapsedInBlockMs, 0), total) / total;
//   }, [activeBlock, elapsedInBlockMs]);

//   // --------------------------------------------------------------------------------
//   // 6. CICLO DE VIDA: CARGA INICIAL
//   // --------------------------------------------------------------------------------
  
//   // Cargar canciones desde la API al montar el componente
//   useEffect(() => {
//     const loadSongs = async () => {
//       try {
//         const response = await fetch(`${process.env.REACT_APP_API_URL}karaoke/songs`);
//         if (response.ok) {
//           const data = await response.json();
//           setSongs(data.songs || []);
//         }
//       } catch (error) {
//         console.error("Error al cargar canciones:", error);
//       }
//     };
//     loadSongs();
//   }, []);

//   // Si cambia la canción seleccionada, detenemos el reproductor
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   // --------------------------------------------------------------------------------
//   // 7. COMUNICACIÓN CON LA API (WEBSOCKETS A PANTALLA)
//   // --------------------------------------------------------------------------------

//   // Enviar configuración de VISIBILIDAD
//   const toggleVisibility = async (isVisible) => {
//     try { await fetch(`${process.env.REACT_APP_API_URL}karaoke/visible?visible=${isVisible}`, { method: 'POST' }); } 
//     catch (e) { console.error(e); }
//   };

//   // Enviar configuración de RELLENO (Activar/Desactivar animación)
//   const toggleFilling = async (isFilling) => {
//     try { await fetch(`${process.env.REACT_APP_API_URL}karaoke/fill?fill=${isFilling}`, { method: 'POST' }); } 
//     catch (e) { console.error(e); }
//   };

//   // Guardar y Enviar COLORES
//   const handleSaveColors = async (newColors) => {
//     setColorSettings(newColors); // Actualizar local
//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/colors`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({ BaseColor: newColors.base, FillColor: newColors.fill, StrokeColor: newColors.stroke })
//       });
//     } catch (e) { console.error("Error guardando colores", e); }
//   };

//   // Enviar la LÍNEA ACTUAL a la pantalla (Se dispara cada vez que cambia activeBlock)
//   const sendCurrentLyricToScreen = async (block) => {
//     if (!block) return;
//     const payload = {
//       text: block.mode === 'uniform' ? block.text : block.parts?.map(p => p.text).join("") || "",
//       mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
//       duration: getBlockDurationMs(block)
//     };

//     try {
//       await fetch(`${process.env.REACT_APP_API_URL}karaoke/lyric`, {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify(payload)
//       });
//     } catch (e) { console.error("Error enviando lyric:", e); }
//   };

//   // Efecto: Cuando cambia el índice del bloque, sincronizamos pantalla y hacemos scroll
//   useEffect(() => {
//     if (activeBlock) {
//       sendCurrentLyricToScreen(activeBlock);
//       scrollToActiveLyric();
//     }
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [activeBlockIndex]); // Dependencia clave: índice del bloque

//   // Helper para hacer scroll automático en la lista de la derecha (Efecto Candado)
//   const scrollToActiveLyric = () => {
//     if (lyricsListRef.current) {
//       const listItems = lyricsListRef.current.children;
//       const activeItem = listItems[activeBlockIndex];
//       if (activeItem) {
//         activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
//       }
//     }
//   };

//   // --------------------------------------------------------------------------------
//   // 8. CONTROLES DEL REPRODUCTOR (PLAY / PAUSE / NAV)
//   // --------------------------------------------------------------------------------

//   const playPlayback = () => {
//     if (!blocks.length) return showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Selecciona una canción válida.' })
//     setIsPlaying(true);
//   };

//   const pausePlayback = () => {
//     setIsPlaying(false);
//     lastFrameTimeRef.current = 0; // Resetear delta time para evitar saltos al reanudar
//   };

//   const stopPlayback = () => {
//     setIsPlaying(false);
//     setActiveBlockIndex(0);
//     setElapsedInBlockMs(0);
//     lastFrameTimeRef.current = 0;
//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//     // Opcional: Enviar señal de "Clear Screen" a la API aquí si se desea
//   };

//   const goToNext = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
//   };

//   const goToPrev = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
//   };
  
//   const goToStart = () => { stopPlayback(); }; // Ir al inicio es equivalente a Stop
  
//   const goToEnd = () => {
//     setElapsedInBlockMs(0);
//     setActiveBlockIndex(blocks.length - 1);
//   };

//   // --------------------------------------------------------------------------------
//   // 9. CORE: LOOP DE ANIMACIÓN (REQUEST ANIMATION FRAME)
//   // --------------------------------------------------------------------------------
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const deltaMs = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs(prev => {
//         const duration = getBlockDurationMs(activeBlock);
//         const nextElapsed = prev + deltaMs;

//         // Si se acabó el tiempo del bloque actual
//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1;
          
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false); // Fin de la canción
//             return duration;     // Dejar lleno al final
//           }
          
//           // Avanzar al siguiente bloque
//           setActiveBlockIndex(nextIdx); // Esto disparará el envío de socket
//           lastFrameTimeRef.current = 0; // Resetear timer para el nuevo bloque
//           return 0; // Resetear tiempo transcurrido
//         }
//         return nextElapsed;
//       });

//       rafIdRef.current = requestAnimationFrame(tick);
//     };

//     rafIdRef.current = requestAnimationFrame(tick);

//     // Cleanup: Cancelar animación si se desmonta o pausa
//     return () => { if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current); };
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);



//   const ReturnToDashboard = async () => {
//     try {
//       await BLResetCurrentDynamic()
//       getOutOfTheDynamic()
//     } catch (error) {
        
//     }
//   }

//   // --------------------------------------------------------------------------------
//   // 10. RENDERIZADO (JSX)
//   // --------------------------------------------------------------------------------
//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//       <div className='Karaoke'>
        
//         {/* === HEADER === */}
//         <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

//         {/* Botón para ir al creador */}
//         <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
//           <i className='bi bi-plus-circle'></i> Crear canción
//         </button>

//         <div className='Karaoke__Container'>
          
//           {/* === PANEL IZQUIERDO: DECK / CONTROL === */}
//           <div className='Karaoke__Container__Deck Element--Shadow'>
            
//             {/* A. Botones de Configuración */}
//             <div className='Karaoke__Container__Deck__SettingButtons'>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => toggleFilling(true)}>
//                 <i className='bi bi-paint-bucket'></i> Rellenar
//               </button>
              
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => setIsColorModalOpen(true)}>
//                 <i className='bi bi-palette'></i> Colores
//               </button>
              
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => toggleVisibility(false)}>
//                 <i className='bi bi-eye-slash'></i> Ocultar
//               </button>
//             </div>

//             {/* B. Selector de Canciones */}
//             <select className='Karaoke__Container__Deck__Playlist' value={selectedSongId} onChange={(e) => setSelectedSongId(e.target.value)}>
//               <option value="">-- Selecciona una canción --</option>
//               {songs.map(song => (
//                 <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
//               ))}
//             </select>

//             {/* C. Preview Visual (Simulación de la Pantalla) */}
//             <div className='Karaoke__Container__Deck__Screen'>
//                <div style={{ position: "relative", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
//                  <div style={{ position: "relative", textAlign: "center", fontSize: 24, fontWeight: "900" }}>
                    
//                     {/* Capa 1: Texto Base (Color Inactivo) */}
//                     <div style={{ color: colorSettings.base, WebkitTextStroke: `1px ${colorSettings.stroke}` }}>
//                         {displayText || "..."}
//                     </div>

//                     {/* Capa 2: Texto Relleno (Color Activo con clip) */}
//                     <div style={{ 
//                         position: "absolute", inset: 0, overflow: "hidden", whiteSpace: "nowrap",
//                         width: `${(fillProgress * 100).toFixed(2)}%`, // Aquí ocurre la magia de la animación
//                         color: colorSettings.fill, 
//                         WebkitTextStroke: `1px ${colorSettings.stroke}`
//                     }}>
//                         {displayText || ""}
//                     </div>

//                  </div>
//                </div>
//             </div>

//             {/* D. Botones de Control (Playback) */}
//             <div className='Karaoke__Container__Deck__ControlButtons'>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Anterior"><i className='bi bi-arrow-left'></i></button>
              
//               {!isPlaying ? (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
//               ) : (
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
//               )}

//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Siguiente"><i className='bi bi-arrow-right'></i></button>
//               <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
//             </div>
//           </div>

//           {/* === PANEL DERECHO: LISTA DE LETRAS === */}
//           <div className='Karaoke__Container__Lyrics Element--Shadow'>
//             {!selectedSong ? (
//                <p style={{textAlign:"center", color:"#999", marginTop: 20}}>Selecciona una canción para ver la letra.</p>
//             ) : (
//                <>
//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={goToStart}><i className='bi bi-chevron-bar-up'></i></button>

//                  <div className='Karaoke__Container__Lyrics__Box'>
//                    <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
//                      {blocks.map((block, index) => {
//                        const text = block.type === 'instrumental' 
//                           ? '🎵 (Instrumental/Solo)' 
//                           : (block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""));
                       
//                        const isActive = index === activeBlockIndex;

//                        return (
//                          <li 
//                            key={block.id || index} 
//                            className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
//                            onClick={() => {
//                              // Click-to-Jump: Saltar directamente a esta línea
//                              setElapsedInBlockMs(0);
//                              setActiveBlockIndex(index);
//                            }}
//                          >
//                            {text}
//                          </li>
//                        );
//                      })}
//                    </ul>
//                  </div>

//                  <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={goToEnd}><i className='bi bi-chevron-bar-down'></i></button>
//                </>
//             )}
//           </div>
//         </div>

//         {/* Modal Configuración de Colores */}
//         <ColorSettingsModal 
//           isOpen={isColorModalOpen}
//           onClose={() => setIsColorModalOpen(false)}
//           currentSettings={colorSettings}
//           onSave={handleSaveColors}
//         />

//       </div>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };


























// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate();

//   // --- ESTADOS DE DATOS ---
//   const [songs, setSongs] = useState([]); // Lista de la BD
//   const [selectedSongId, setSelectedSongId] = useState(""); 
  
//   // --- ESTADOS DEL REPRODUCTOR LOCAL (PREVIEW) ---
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0);
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0);
  
//   // --- REFS PARA ANIMACIÓN ---
//   const rafIdRef = useRef(null);
//   const lastFrameTimeRef = useRef(0);
  
//   // Referencia para el scroll automático de la lista de letras
//   const lyricsListRef = useRef(null); 

//   // --- COMPUTADOS (Helpers para encontrar la canción actual) ---
//   const selectedSong = useMemo(() => songs.find(s => s.id === selectedSongId), [songs, selectedSongId]);
//   const blocks = useMemo(() => selectedSong?.lyrics || [], [selectedSong]);
//   const activeBlock = blocks[activeBlockIndex] || null;

//   const [isColorModalOpen, setIsColorModalOpen] = useState(false);
//   const [colorSettings, setColorSettings] = useState({
//     base: '#ffffff',
//     fill: '#ffd400',
//     stroke: '#000000'
//   });

//   // ============================================================
//   // 1. CARGA DE DATOS (API DATABASE)
//   // ============================================================
//   const loadSongsFromDb = async () => {
//     try {
//       const response = await fetch('/api/karaoke/songs');
//       if (response.ok) {
//         const data = await response.json();
//         // data.songs viene del formato que definimos en el controlador C#
//         setSongs(data.songs || []);
//       } else {
//         console.error("Error cargando canciones");
//       }
//     } catch (error) {
//       console.error("Error de conexión:", error);
//     }
//   };

//   useEffect(() => {
//     loadSongsFromDb();
//   }, []);

//   // Al cambiar canción, reseteamos todo
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   // ============================================================
//   // 2. WEBSOCKETS SENDERS (Conectando con tu Controlador)
//   // ============================================================
  
//   // Enviar configuración de VISIBILIDAD
//   const toggleVisibility = async (isVisible) => {
//     try {
//         await fetch(`/api/karaoke/visible?visible=${isVisible}`, { method: 'POST' });
//     } catch (e) { console.error(e); }
//   };

//   // Enviar configuración de RELLENO (Fill)
//   const toggleFilling = async (isFilling) => {
//     try {
//         await fetch(`/api/karaoke/fill?fill=${isFilling}`, { method: 'POST' });
//     } catch (e) { console.error(e); }
//   };

//   // Enviar LÍNEA ACTUAL a la Pantalla
//   const sendCurrentLyricToScreen = async (block) => {
//     if (!block) return;
    
//     // Armamos el objeto que espera tu endpoint SendLyricToScreen
//     const payload = {
//         text: block.mode === 'uniform' ? block.text : block.parts.map(p => p.text).join(""),
//         mode: block.type === 'instrumental' ? 'instrumental' : 'lyric',
//         // Puedes agregar más datos aquí si tu endpoint los requiere (colores, fuentes)
//     };

//     try {
//         await fetch('/api/karaoke/lyric', { 
//             method: 'POST',
//             headers: { 'Content-Type': 'application/json' },
//             body: JSON.stringify(payload)
//         });
//     } catch (e) { console.error("Error enviando lyric socket", e); }
//   };

//   // Cada vez que cambia el bloque activo, enviamos la señal a la pantalla (si está reproduciendo)
//   useEffect(() => {
//      if(activeBlock) {
//          sendCurrentLyricToScreen(activeBlock);
//          scrollToActiveLyric(); // Auto-scroll lista derecha
//      }
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [activeBlockIndex]); // Se dispara cuando cambia el índice

//   // ============================================================
//   // 3. LÓGICA DE REPRODUCCIÓN (PLAY/PAUSE/NEXT)
//   // ============================================================
  
//   const playPlayback = () => {
//       if(!blocks.length) return;
//       setIsPlaying(true);
//       // Aquí podrías enviar un socket de "Play" si tu pantalla maneja el tiempo independientemente,
//       // pero por ahora la pantalla es "esclava" de la línea que le mandamos.
//   };

//   const pausePlayback = () => {
//       setIsPlaying(false);
//       lastFrameTimeRef.current = 0;
//   };

//   const stopPlayback = () => {
//       setIsPlaying(false);
//       setActiveBlockIndex(0);
//       setElapsedInBlockMs(0);
//       lastFrameTimeRef.current = 0;
//       if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//       // Opcional: Enviar socket de "Limpiar pantalla" o ir a inicio
//   };

//   const goToPrev = () => {
//       setElapsedInBlockMs(0);
//       setActiveBlockIndex(prev => (prev - 1 < 0 ? 0 : prev - 1));
//   };

//   const goToNext = () => {
//       setElapsedInBlockMs(0);
//       setActiveBlockIndex(prev => (prev + 1 >= blocks.length ? 0 : prev + 1));
//   };
  
//   const goToStart = () => {
//       stopPlayback(); // Índice 0
//   };
  
//   const goToEnd = () => {
//       setElapsedInBlockMs(0);
//       setActiveBlockIndex(blocks.length - 1);
//   };

//   // Scroll automático para la lista de la derecha ("Candado")
//   const scrollToActiveLyric = () => {
//       if (lyricsListRef.current) {
//           const activeItem = lyricsListRef.current.children[activeBlockIndex];
//           if (activeItem) {
//               activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
//           }
//       }
//   };

//   // LOOP DE ANIMACIÓN (TICK) - Mantiene el tiempo corriendo
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const delta = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs(prev => {
//         // Lógica simplificada para obtener duración
//         const duration = activeBlock.durationMs || 0; 
//         const nextElapsed = prev + delta;

//         if (duration > 0 && nextElapsed >= duration) {
//           const nextIdx = activeBlockIndex + 1;
//           if (nextIdx >= blocks.length) {
//             setIsPlaying(false);
//             return duration;
//           }
//           setActiveBlockIndex(nextIdx); // Esto disparará el useEffect que envía el Socket
//           lastFrameTimeRef.current = 0;
//           return 0;
//         }
//         return nextElapsed;
//       });
//       rafIdRef.current = requestAnimationFrame(tick);
//     };
//     rafIdRef.current = requestAnimationFrame(tick);
//     return () => cancelAnimationFrame(rafIdRef.current);
//   }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]);



//   const ReturnToDashboard = async () => {
//     try {
//         await BLResetCurrentDynamic()
//         getOutOfTheDynamic()
//     } catch (error) {
        
//     }
//   }

//   const handleSaveColors = async (newColors) => {
//     setColorSettings(newColors); // Actualizamos localmente para el Deck Preview
    
//     // Enviamos al Backend
//     try {
//       await fetch('/api/karaoke/colors', {
//         method: 'POST',
//         headers: { 'Content-Type': 'application/json' },
//         body: JSON.stringify({
//            BaseColor: newColors.base,
//            FillColor: newColors.fill,
//            StrokeColor: newColors.stroke
//         })
//       });
//     } catch (error) {
//       console.error("Error guardando colores", error);
//     }
//   };

//   // ============================================================
//   // RENDER (Tu estructura HTML adaptada)
//   // ============================================================
//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//       <div className='Karaoke'>
//           <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

//           <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}>
//               <i className='bi bi-plus-circle'></i> Crear canción
//           </button>

//           <div className='Karaoke__Container'>
            
//             {/* --- DECK (IZQUIERDA) --- */}
//             <div className='Karaoke__Container__Deck Element--Shadow'>
              
//               {/* Botones Configuración */}
//               <div className='Karaoke__Container__Deck__SettingButtons'>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => toggleFilling(true)}>
//                     <i className='bi bi-paint-bucket'></i> Rellenar
//                 </button>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'>
//                     <i className='bi bi-palette'></i> Colores
//                 </button>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons' onClick={() => toggleVisibility(false)}>
//                     <i className='bi bi-eye-slash'></i> Ocultar
//                 </button>
//               </div>

//               {/* Select de Playlist (Cargado de BD) */}
//               <select 
//                 className='Karaoke__Container__Deck__Playlist'
//                 value={selectedSongId}
//                 onChange={(e) => setSelectedSongId(e.target.value)}
//               >
//                 <option value="">Selecciona una canción</option>
//                 {songs.map(song => (
//                     <option key={song.id} value={song.id}>{song.name} - {song.artist}</option>
//                 ))}
//               </select>

//               {/* Screen Preview */}
//               <div className='Karaoke__Container__Deck__Screen'>
//                   <div style={{ padding: 20, textAlign: "center", color: "#fff", fontWeight: "bold", fontSize: 24 }}>
//                      {activeBlock ? (
//                          activeBlock.mode === 'uniform' ? activeBlock.text : activeBlock.parts?.map(p=>p.text).join("") 
//                      ) : "..."}
//                   </div>
//               </div>

//               {/* Botones Control (Reproductor) */}
//               <div className='Karaoke__Container__Deck__ControlButtons'>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToStart} title="Inicio"><i className='bi bi-chevron-bar-left'></i></button>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToPrev} title="Anterior"><i className='bi bi-arrow-left'></i></button>
                
//                 {!isPlaying ? (
//                     <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={playPlayback} title="Reproducir"><i className='bi bi-play-fill'></i></button>
//                 ) : (
//                     <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={pausePlayback} title="Pausar"><i className='bi bi-pause-fill'></i></button>
//                 )}

//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={stopPlayback} title="Detener"><i className='bi bi-stop-fill'></i></button>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToNext} title="Siguiente"><i className='bi bi-arrow-right'></i></button>
//                 <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons' onClick={goToEnd} title="Fin"><i className='bi bi-chevron-bar-right'></i></button>
//               </div>
//             </div>

//             {/* --- LYRICS LIST (DERECHA) --- */}
//             <div className='Karaoke__Container__Lyrics Element--Shadow'>
//                 {!selectedSong ? (
//                     <p style={{textAlign:"center", color:"#999"}}>Selecciona una canción para cargar la letra</p>
//                 ) : (
//                     <>
//                         {/* Botón Scroll Arriba (Inicio) */}
//                         <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={goToStart}>
//                             <i className='bi bi-chevron-bar-up'></i>
//                         </button>

//                         <div className='Karaoke__Container__Lyrics__Box'>
//                           <ul className='Karaoke__Container__Lyrics__Box__List' ref={lyricsListRef}>
//                             {blocks.map((block, index) => {
//                                 // Texto a mostrar en la lista
//                                 const text = block.type === 'instrumental' 
//                                     ? '🎵 (Instrumental)' 
//                                     : (block.mode === 'uniform' ? block.text : block.parts.map(p=>p.text).join(""));
                                
//                                 const isActive = index === activeBlockIndex;
                                
//                                 return (
//                                     <li 
//                                         key={block.id || index} 
//                                         className={`Karaoke__Container__Lyrics__Box__List__Items ${isActive ? 'Karaoke__Container__Lyrics__Box__List__Items--Active' : ''}`}
//                                         onClick={() => {
//                                             // Click-to-Jump: Saltar a esta línea
//                                             setElapsedInBlockMs(0);
//                                             setActiveBlockIndex(index);
//                                         }}
//                                     >
//                                         {text}
//                                     </li>
//                                 );
//                             })}
//                           </ul>
//                         </div>

//                         {/* Botón Scroll Abajo (Fin) */}
//                         <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons' onClick={goToEnd}>
//                             <i className='bi bi-chevron-bar-down'></i>
//                         </button>
//                     </>
//                 )}
//             </div>

//           </div>
//       </div>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };


























// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const navigate = useNavigate()

  
//   const ReturnToDashboard = async () => {
//       try {
//           await BLResetCurrentDynamic()
//           getOutOfTheDynamic()
//       } catch (error) {
          
//       }
//   }

//   const LoadPage = () => {
//     try {
      
//     } catch (error) {
      
//     }
//   }

//   useEffect(() => {
//     LoadPage()
//   },[])
  

//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//         <div className='Karaoke'>
//             <p className='Karaoke__Text--Title'><i className='bi bi-file-music'></i> Karaoke</p>

//             <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Buttons--Center' onClick={() => navigate('/admin/karaoke/create')}><i className='bi bi-plus-circle'></i> Crear canción</button>

//             <div className='Karaoke__Container'>
//               {/* Vista previa */}
//               <div className='Karaoke__Container__Deck Element--Shadow'>
//                 <div className='Karaoke__Container__Deck__SettingButtons'>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'><i className='bi bi-paint-bucket'></i> Rellenar</button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'><i className='bi bi-palette'></i> Colores</button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__SettingButtons__Buttons'><i className='bi bi-eye-slash'></i> Ocultar</button>
//                 </div>

//                 <select className='Karaoke__Container__Deck__Playlist'>
//                   <option className='Karaoke__Container__Deck__Playlist__Songs'>Selecciona una canción</option>
//                   <option className='Karaoke__Container__Deck__Playlist__Songs'>Canción #1</option>
//                   <option className='Karaoke__Container__Deck__Playlist__Songs'>Canción #2</option>
//                   <option className='Karaoke__Container__Deck__Playlist__Songs'>Canción #3</option>
//                 </select>

//                 <div className='Karaoke__Container__Deck__Screen'>
//                     {/* Screen o rectángulo con letras */}
//                     <p>Selecciona una canción</p>
//                 </div>

//                 <div className='Karaoke__Container__Deck__ControlButtons'>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-chevron-bar-left'></i></button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-arrow-left'></i></button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-play-fill'></i></button>
//                   {/* <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-pause'></i></button> */}
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-stop-fill'></i></button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-arrow-right'></i></button>
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Deck__ControlButtons__Buttons'><i className='bi bi-chevron-bar-right'></i></button>
//                 </div>
//               </div>

//               {/* Lista de letras */}
//               <div className='Karaoke__Container__Lyrics Element--Shadow'>

//                   {/* Mostrar este elemento cuando no haya cargado una canción aún */}
//                   <p>Selecciona una canción para cargar la letra aquí</p>

//                   {/* Ejemplo de estructura cuando ya se cargó una canción  */}
//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons'><i className='bi bi-chevron-bar-up'></i></button>

//                   <div className='Karaoke__Container__Lyrics__Box'>
//                     <ul className='Karaoke__Container__Lyrics__Box__List'>
//                       <li className='Karaoke__Container__Lyrics__Box__List__Items'>Línea de la canción</li>
//                       <li className='Karaoke__Container__Lyrics__Box__List__Items'>Línea de la canción</li>
//                       <li className='Karaoke__Container__Lyrics__Box__List__Items Karaoke__Container__Lyrics__Box__List__Items--Active'>Línea de la canción</li>
//                       <li className='Karaoke__Container__Lyrics__Box__List__Items'>Línea de la canción</li>
//                       <li className='Karaoke__Container__Lyrics__Box__List__Items'>Línea de la canción</li>
//                     </ul>
//                   </div>

//                   <button type='button' className='Btn__Adsl--RedOutlineTransparent Karaoke__Container__Lyrics__Buttons'><i className='bi bi-chevron-bar-down'></i></button>

//               </div>
//             </div>
//         </div>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };



























// import React, { useEffect, useMemo, useRef, useState } from 'react'

// import { GetDynamicData as BLGetDynamicData } from './../../../utilities/BL/Admin/Dashboard/Dynamics'
// import { SendQuestionToUsers as BLSendQuestionToUsers, GetAllResults, ResetCurrentDynamic as BLResetCurrentDynamic, SendResultsToScreen } from './../../../utilities/BL/Zones'

// import { SecondaryBase } from './../Commons/SecondaryBase'

// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'


// // Este será el “componente hijo” (te lo doy en el siguiente archivo)
// import { KaraokeSongCreator } from './Karaoke/KaraokeSongCreator'

// /**
//  * ============================================================
//  * Karaoke.jsx
//  * ------------------------------------------------------------
//  * Este componente será el "hub" principal del Karaoke:
//  * 1) Reproductor/Preview (uso frecuente)
//  * 2) Acceso al Creador/Editor de canciones (uso ocasional)
//  *
//  * En esta primera versión:
//  * - Las canciones se guardan en localStorage
//  * - La estructura soporta:
//  *   - lyric/uniform
//  *   - lyric/parts
//  *   - instrumental (gap)
//  *
//  * El siguiente archivo (KaraokeSongCreator.jsx) implementará:
//  * - Crear/editar/borrar canciones
//  * - Export/Import JSON
//  * - Autosave
//  * - Validaciones
//  * ============================================================
//  */

// /** ============================================================
//  * 1) LOCAL STORAGE: KEYS + HELPERS
//  * ============================================================ */
// const LOCAL_STORAGE_KEY = "karaoke_songs_v1";

//     /**
//    * HELPERS DE TIEMPO
//    */
//   const formatTime = (ms) => {
//     if (!ms || ms < 0) return "00:00";
//     const totalSeconds = Math.floor(ms / 1000);
//     const m = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
//     const s = (totalSeconds % 60).toString().padStart(2, "0");
//     return `${m}:${s}`;
//   };

//   // Duración total de la canción (Suma de todos los bloques)
//   const totalDurationMs = useMemo(() => {
//     return blocks.reduce((acc, block) => acc + getBlockDurationMs(block), 0);
//   }, [blocks]);

//   // Tiempo acumulado HASTA el bloque actual (sin contar el progreso actual)
//   const previousBlocksDurationMs = useMemo(() => {
//     let sum = 0;
//     for (let i = 0; i < activeBlockIndex; i++) {
//       sum += getBlockDurationMs(blocks[i]);
//     }
//     return sum;
//   }, [blocks, activeBlockIndex]);

//   // Tiempo actual real (Acumulado + Progreso en bloque actual)
//   const currentPositionMs = previousBlocksDurationMs + elapsedInBlockMs;

//   /**
//    * LÓGICA DE ANIMACIÓN CORREGIDA
//    * Eliminamos setTimeout y mejoramos el control del frame
//    */
//   useEffect(() => {
//     if (!isPlaying || !activeBlock) return;

//     let animationFrameId;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;
//       const deltaMs = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs((prev) => {
//         const duration = getBlockDurationMs(activeBlock);
//         const nextElapsed = prev + deltaMs;

//         // Si el bloque terminó
//         if (duration > 0 && nextElapsed >= duration) {
//           // Detenemos este ciclo inmediatamente
//           // La actualización de estado disparará un re-render y un nuevo efecto
//           // para el siguiente bloque.
          
//           // Calculamos cuál es el siguiente índice
//           const nextIndex = activeBlockIndex + 1;
          
//           if (nextIndex >= blocks.length) {
//              // Fin de la canción
//              setIsPlaying(false);
//              return duration; // Se queda lleno al final
//           } else {
//              // Siguiente bloque
//              setActiveBlockIndex(nextIndex);
//              lastFrameTimeRef.current = 0; // Resetear tiempo frame
//              return 0; // Resetear elapsed del nuevo bloque
//           }
//         }
//         return nextElapsed;
//       });

//       animationFrameId = requestAnimationFrame(tick);
//     };

//     animationFrameId = requestAnimationFrame(tick);

//     return () => {
//       cancelAnimationFrame(animationFrameId);
//     };
// }, [isPlaying, activeBlockIndex, activeBlock, blocks.length]); // Dependencias estrictas

// /**
//  * Genera UUID usando crypto si existe (navegadores modernos),
//  * con fallback por si algún entorno lo bloquea.
//  */
// function generateUuid() {
//   if (typeof crypto !== "undefined" && crypto.randomUUID) {
//     return crypto.randomUUID();
//   }

//   // Fallback simple (suficiente para uso local)
//   return `uuid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
// }

// /**
//  * Lee el "payload" completo desde localStorage.
//  * Formato recomendado:
//  * {
//  *   version: 1,
//  *   songs: Song[]
//  * }
//  */
// function readSongsPayloadFromLocalStorage() {
//   try {
//     const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
//     if (!raw) return { version: 1, songs: [] };

//     const parsed = JSON.parse(raw);

//     // Aseguramos una estructura mínima válida
//     const version = typeof parsed?.version === "number" ? parsed.version : 1;
//     const songs = Array.isArray(parsed?.songs) ? parsed.songs : [];

//     return { version, songs };
//   } catch (error) {
//     // Si algo se corrompió en localStorage, regresamos vacío
//     return { version: 1, songs: [] };
//   }
// }

// /**
//  * Guarda el payload completo en localStorage.
//  */
// function writeSongsPayloadToLocalStorage(payload) {
//   localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
// }

// /**
//  * Devuelve la lista de canciones.
//  */
// function getAllSongsFromLocalStorage() {
//   const payload = readSongsPayloadFromLocalStorage();
//   return payload.songs;
// }

// /**
//  * Reemplaza toda la lista de canciones.
//  */
// function setAllSongsInLocalStorage(songs) {
//   const payload = readSongsPayloadFromLocalStorage();
//   writeSongsPayloadToLocalStorage({ ...payload, songs });
// }

// /**
//  * ============================================================
//  * 2) MODELO (DOCUMENTACIÓN)
//  * ============================================================
//  *
//  * Song:
//  * {
//  *   id: string (uuid)
//  *   name: string
//  *   artist: string
//  *   url: string
//  *   createdAt: string (ISO)
//  *   updatedAt: string (ISO)
//  *   lyrics: KaraokeBlock[]
//  * }
//  *
//  * KaraokeBlock:
//  * - Lyric Uniform:
//  *   { id, type:"lyric", mode:"uniform", text, durationMs }
//  *
//  * - Lyric Parts:
//  *   { id, type:"lyric", mode:"parts", parts:[ { text, durationMs }, ... ] }
//  *
//  * - Instrumental:
//  *   { id, type:"instrumental", display:"none"|"icon", durationMs }
//  *
//  * Nota:
//  * - Cada bloque lleva su propio id (uuid) para poder mover/borrar/editar fácil.
//  */

// /**
//  * Crea una canción de ejemplo para que no esté vacío al inicio.
//  * (Puedes quitarlo si prefieres iniciar vacío.)
//  */
// function createSampleSong() {
//   const nowIso = new Date().toISOString();

//   return {
//     id: generateUuid(),
//     name: "Canción de ejemplo",
//     artist: "Artista",
//     url: "https://youtube.com/",
//     createdAt: nowIso,
//     updatedAt: nowIso,
//     lyrics: [
//       {
//         id: generateUuid(),
//         type: "lyric",
//         mode: "uniform",
//         text: "Tal vez me olvide de ti",
//         durationMs: 2200,
//       },
//       {
//         id: generateUuid(),
//         type: "lyric",
//         mode: "uniform",
//         text: "tal vez me olvide de mí",
//         durationMs: 2100,
//       },
//       {
//         id: generateUuid(),
//         type: "instrumental",
//         display: "icon", // "none" para ocultar totalmente
//         durationMs: 1200,
//       },
//       {
//         id: generateUuid(),
//         type: "lyric",
//         mode: "parts",
//         parts: [
//           { text: "viviendo en t", durationMs: 2000 },
//           { text: "iiiiii", durationMs: 1600 },
//         ],
//       },
//     ],
//   };
// }

// /**
//  * ============================================================
//  * 3) KARAOKE PLAYER (LÓGICA DE REPRODUCCIÓN)
//  * ============================================================
//  *
//  * Idea del Player:
//  * - Un “cursor” avanza por bloques (lyrics).
//  * - Cada bloque tiene una duración total:
//  *   - uniform: durationMs
//  *   - parts: sum(parts[].durationMs)
//  *   - instrumental: durationMs
//  * - La UI muestra:
//  *   - Si lyric: el texto base (blanco) y el fill (amarillo) según progreso
//  *   - Si instrumental:
//  *     - display none: ocultar texto
//  *     - display icon: mostrar "♪" (o similar)
//  */

// /**
//  * Calcula la duración total del bloque.
//  */
// function getBlockDurationMs(block) {
//   if (!block) return 0;

//   if (block.type === "instrumental") {
//     return Math.max(0, Number(block.durationMs) || 0);
//   }

//   // lyric
//   if (block.mode === "uniform") {
//     return Math.max(0, Number(block.durationMs) || 0);
//   }

//   // lyric parts
//   const parts = Array.isArray(block.parts) ? block.parts : [];
//   return parts.reduce((acc, p) => acc + Math.max(0, Number(p.durationMs) || 0), 0);
// }

// /**
//  * Construye el texto “visible” de un bloque:
//  * - lyric uniform: block.text
//  * - lyric parts: concatenación de parts.text
//  * - instrumental:
//  *   - display icon: "♪"
//  *   - display none: ""
//  */
// function getBlockDisplayText(block) {
//   if (!block) return "";

//   if (block.type === "instrumental") {
//     return block.display === "icon" ? "♪" : "";
//   }

//   // lyric
//   if (block.mode === "uniform") {
//     return block.text ?? "";
//   }

//   const parts = Array.isArray(block.parts) ? block.parts : [];
//   return parts.map((p) => p.text ?? "").join("");
// }

// /**
//  * Calcula el progreso (0..1) del “fill” para un bloque:
//  * - uniform: elapsed / duration
//  * - parts: se reparte por partes, respetando duraciones
//  */
// function getFillProgressForBlock(block, elapsedMs) {
//   const total = getBlockDurationMs(block);
//   if (total <= 0) return 0;

//   const clampedElapsed = Math.min(Math.max(elapsedMs, 0), total);

//   // instrumental: no queremos “fill amarillo” (se puede dejar 0 o 1)
//   if (block.type === "instrumental") return 1;

//   if (block.mode === "uniform") {
//     return clampedElapsed / total;
//   }

//   // parts: progreso basado en el total, pero la percepción “lento al final”
//   // se logra porque las partes finales tienen más durationMs.
//   return clampedElapsed / total;
// }

// /**
//  * ============================================================
//  * 4) COMPONENTE
//  * ============================================================ */
// const Karaoke = ({ getOutOfTheDynamic }) => {
//   const { showMessage } = useFloatMessage();

//   // ---- Biblioteca (localStorage) ----
//   const [songs, setSongs] = useState([]);
//   const [selectedSongId, setSelectedSongId] = useState(null);

//   // ---- UI: abrir/cerrar creador ----
//   const [isCreatorOpen, setIsCreatorOpen] = useState(false);

//   // ---- Player: estado de reproducción ----
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [activeBlockIndex, setActiveBlockIndex] = useState(0);

//   // “elapsed” dentro del bloque actual (ms)
//   const [elapsedInBlockMs, setElapsedInBlockMs] = useState(0);

//   // Referencias de animación (requestAnimationFrame)
//   const rafIdRef = useRef(null);
//   const lastFrameTimeRef = useRef(0);

//   /** Song seleccionada */
//   const selectedSong = useMemo(() => {
//     return songs.find((s) => s.id === selectedSongId) ?? null;
//   }, [songs, selectedSongId]);

//   /** Bloques de la canción seleccionada */
//   const blocks = useMemo(() => {
//     return Array.isArray(selectedSong?.lyrics) ? selectedSong.lyrics : [];
//   }, [selectedSong]);

//   /** Bloque activo */
//   const activeBlock = useMemo(() => {
//     return blocks[activeBlockIndex] ?? null;
//   }, [blocks, activeBlockIndex]);

//   /** Texto visible en pantalla */
//   const displayText = useMemo(() => {
//     return getBlockDisplayText(activeBlock);
//   }, [activeBlock]);

//   /** Progreso del fill (0..1) */
//   const fillProgress = useMemo(() => {
//     return getFillProgressForBlock(activeBlock, elapsedInBlockMs);
//   }, [activeBlock, elapsedInBlockMs]);

//   /**
//    * Carga inicial:
//    * - lee canciones de localStorage
//    * - si no hay, crea una canción de ejemplo (opcional)
//    * - selecciona la primera
//    */
//   const loadSongsFromLocalStorage = async () => {
//     try {
//       const storedSongs = getAllSongsFromLocalStorage();

//       if (!storedSongs.length) {
//         const sample = createSampleSong();
//         setAllSongsInLocalStorage([sample]);
//         setSongs([sample]);
//         setSelectedSongId(sample.id);
//         return;
//       }

//       setSongs(storedSongs);
//       setSelectedSongId((prev) => prev ?? storedSongs[0].id);
//     } catch (error) {
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "No se pudieron cargar las canciones desde localStorage.",
//       });
//     }
//   };

//   /**
//    * Guarda la lista actual en localStorage.
//    * (El creador hijo lo va a usar intensamente)
//    */
//   const persistSongsToLocalStorage = (nextSongs) => {
//     setAllSongsInLocalStorage(nextSongs);
//     setSongs(nextSongs);
//   };

//   /** Regresar al Dashboard (tu flujo existente) */
//   const ReturnToDashboard = async () => {
//     try {
//       await BLResetCurrentDynamic();
//       getOutOfTheDynamic();
//     } catch (error) {
//       // No sabemos tu sistema de logs aquí; mostramos mensaje genérico
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "Ocurrió un error al salir del Karaoke.",
//       });
//     }
//   };

//   /** Inicialización al montar */
//   useEffect(() => {
//     // Mantengo tu clase BodyComplete
//     document.getElementById("root")?.classList.add("BodyComplete");

//     loadSongsFromLocalStorage();

//     showMessage({
//       type: MESSAGE_TYPE.SUCCESS,
//       text: "Karaoke listo.",
//     });

//     // Cleanup: cancelar animación al desmontar
//     return () => {
//       if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//       rafIdRef.current = null;
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   /**
//    * ============================================================
//    * PLAYER CONTROLS
//    * ============================================================ */

//   const stopPlayback = () => {
//     setIsPlaying(false);
//     setActiveBlockIndex(0);
//     setElapsedInBlockMs(0);
//     lastFrameTimeRef.current = 0;

//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//     rafIdRef.current = null;
//   };

//   const pausePlayback = () => {
//     setIsPlaying(false);
//     lastFrameTimeRef.current = 0;

//     if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//     rafIdRef.current = null;
//   };

//   const playPlayback = () => {
//     if (!selectedSong || !blocks.length) {
//       showMessage({
//         type: MESSAGE_TYPE.BAD,
//         text: "No hay canción seleccionada o no tiene bloques.",
//       });
//       return;
//     }

//     setIsPlaying(true);
//   };

//   const goToNextBlock = () => {
//     if (!blocks.length) return;

//     setElapsedInBlockMs(0);
//     setActiveBlockIndex((prev) => {
//       const next = prev + 1;
//       return next >= blocks.length ? 0 : next; // loop
//     });
//   };

//   const goToPrevBlock = () => {
//     if (!blocks.length) return;

//     setElapsedInBlockMs(0);
//     setActiveBlockIndex((prev) => {
//       const next = prev - 1;
//       return next < 0 ? blocks.length - 1 : next;
//     });
//   };

//   /**
//    * Loop de animación:
//    * - Avanza elapsedInBlockMs
//    * - Si excede la duración, pasa al siguiente bloque
//    */
//   useEffect(() => {
//     if (!isPlaying) return;
//     if (!activeBlock) return;

//     const tick = (now) => {
//       if (!lastFrameTimeRef.current) lastFrameTimeRef.current = now;

//       const deltaMs = now - lastFrameTimeRef.current;
//       lastFrameTimeRef.current = now;

//       setElapsedInBlockMs((prev) => {
//         const duration = getBlockDurationMs(activeBlock);

//         // Si duración 0, saltamos inmediatamente al siguiente
//         if (duration <= 0) return prev;

//         const nextElapsed = prev + deltaMs;

//         if (nextElapsed >= duration) {
//           // saltar al siguiente bloque
//           // IMPORTANTE: hacemos el cambio de bloque aquí, pero el state update
//           // del índice ocurre por separado con goToNextBlock()
//           // Para evitar “doble salto”, hacemos:
//           setTimeout(goToNextBlock, 0);
//           return 0;
//         }

//         return nextElapsed;
//       });

//       rafIdRef.current = requestAnimationFrame(tick);
//     };

//     rafIdRef.current = requestAnimationFrame(tick);

//     // Cleanup si se pausa
//     return () => {
//       if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
//       rafIdRef.current = null;
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [isPlaying, activeBlockIndex, selectedSongId]); // dependemos del bloque/canción

//   /**
//    * Si el usuario cambia de canción:
//    * - detenemos/reiniciamos el player para no quedar “a mitad”
//    */
//   useEffect(() => {
//     stopPlayback();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [selectedSongId]);

//   /**
//    * ============================================================
//    * UI HELPERS
//    * ============================================================ */

//   const openCreator = () => setIsCreatorOpen(true);
//   const closeCreator = () => setIsCreatorOpen(false);

//   /**
//    * Lógica que usará el componente hijo para:
//    * - crear / actualizar / borrar
//    * - export / import
//    * - autosave
//    *
//    * En este archivo, solo preparamos el “contrato”:
//    * onSongsChanged(nextSongs)
//    */
//   const onSongsChanged = (nextSongs, optionalSelectedSongId = null) => {
//     persistSongsToLocalStorage(nextSongs);

//     // Si el creador dice “selecciona esta”, lo respetamos
//     if (optionalSelectedSongId) {
//       setSelectedSongId(optionalSelectedSongId);
//     } else if (!nextSongs.find((s) => s.id === selectedSongId)) {
//       // Si borraron la canción seleccionada, seleccionamos la primera
//       setSelectedSongId(nextSongs[0]?.id ?? null);
//     }
//   };

//   /**
//    * ============================================================
//    * RENDER
//    * ============================================================ */
//   return (
//     <SecondaryBase actionToExecute={ReturnToDashboard}>
//       <section style={{ padding: 16 }}>
//         <p style={{ fontWeight: 800, marginBottom: 12 }}>Karaoke</p>

//         {/* ======================================================
//             A) CONTROLES SUPERIORES (Player + Abrir Creador)
//            ====================================================== */}
//         <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
//           {/* Selector de canción */}
//           <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
//             <label style={{ fontWeight: 700 }}>Canción:</label>
//             <select
//               value={selectedSongId ?? ""}
//               onChange={(e) => setSelectedSongId(e.target.value)}
//               style={{ padding: "8px 10px" }}
//             >
//               {songs.map((song) => (
//                 <option key={song.id} value={song.id}>
//                   {song.name} — {song.artist}
//                 </option>
//               ))}
//             </select>
//           </div>

//           {/* Controles de reproducción */}
//           <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
//             <button type="button" onClick={goToPrevBlock}>
//               ◀ Anterior
//             </button>

//             {!isPlaying ? (
//               <button type="button" onClick={playPlayback}>
//                 ▶ Play
//               </button>
//             ) : (
//               <button type="button" onClick={pausePlayback}>
//                 ⏸ Pausa
//               </button>
//             )}

//             <button type="button" onClick={stopPlayback}>
//               ⟲ Reset
//             </button>

//             <button type="button" onClick={goToNextBlock}>
//               Siguiente ▶
//             </button>
//           </div>

//           {/* Abrir creador */}
//           <div style={{ marginLeft: "auto" }}>
//             <button type="button" onClick={openCreator}>
//               + Abrir Creador de Canciones
//             </button>
//           </div>
//         </div>

//         {/* ======================================================
//             B) PREVIEW DEL KARAOKE (BARRA SUPERIOR)
//            ====================================================== */}
//         <div style={{ marginTop: 18 }}>
//           <div
//             style={{
//               position: "relative",
//               width: "100%",
//               height: 120,
//               background: "rgba(0,0,0,0)", // transparente
//               display: "flex",
//               alignItems: "center",
//               justifyContent: "center",
//               overflow: "hidden",
//             }}
//           >
//             <div style={{ position: "relative", maxWidth: "96%", whiteSpace: "nowrap", overflow: "hidden" }}>
//               {/* Texto base (blanco) */}
//               <div
//                 style={{
//                   fontWeight: 900,
//                   letterSpacing: "0.5px",
//                   fontSize: "clamp(34px, 4vw, 72px)",
//                   lineHeight: 1.05,
//                   color: "#fff",
//                   WebkitTextStroke: "3px rgba(0,0,0,0.9)",
//                   userSelect: "none",
//                   textAlign: "center",
//                 }}
//               >
//                 {displayText}
//               </div>

//               {/* Texto fill (amarillo) encima, recortado por width */}
//               <div
//                 style={{
//                   position: "absolute",
//                   inset: 0,
//                   fontWeight: 900,
//                   letterSpacing: "0.5px",
//                   fontSize: "clamp(34px, 4vw, 72px)",
//                   lineHeight: 1.05,
//                   color: "#ffd400",
//                   WebkitTextStroke: "3px rgba(0,0,0,0.9)",
//                   userSelect: "none",
//                   overflow: "hidden",
//                   width: `${(fillProgress * 100).toFixed(3)}%`,
//                   whiteSpace: "nowrap",
//                 }}
//               >
//                 {displayText}
//               </div>
//             </div>
//           </div>

//           {/* Indicador simple de estado */}
//           <div style={{ marginTop: 8, fontSize: 13, opacity: 0.8 }}>
//             <span style={{ fontWeight: 700 }}>Bloque:</span>{" "}
//             {blocks.length ? `${activeBlockIndex + 1} / ${blocks.length}` : "—"}{" "}
//             <span style={{ marginLeft: 10, fontWeight: 700 }}>Modo:</span>{" "}
//             {activeBlock?.type === "instrumental"
//               ? `Instrumental (${activeBlock?.display ?? "none"})`
//               : activeBlock?.mode
//               ? `Lyric (${activeBlock.mode})`
//               : "—"}
//           </div>
//         </div>

//         {/* ======================================================
//             C) CREADOR (COMPONENTE HIJO)
//             - Lo mostramos/ocultamos con isCreatorOpen
//             - En el siguiente archivo te doy la implementación completa
//            ====================================================== */}
//         {isCreatorOpen && (
//           <div style={{ marginTop: 18, padding: 14, border: "1px solid rgba(0,0,0,0.12)", borderRadius: 10 }}>
//             <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
//               <p style={{ fontWeight: 800, margin: 0 }}>Creador de Canciones</p>
//               <button type="button" onClick={closeCreator}>
//                 Cerrar
//               </button>
//             </div>

//             <div style={{ marginTop: 12 }}>
//               <KaraokeSongCreator
//                 songs={songs}
//                 selectedSongId={selectedSongId}
//                 onSongsChanged={onSongsChanged}
//                 onRequestClose={closeCreator}
//                 generateUuid={generateUuid}
//               />
//             </div>
//           </div>
//         )}
//       </section>
//     </SecondaryBase>
//   );
// };

// export { Karaoke };


















// const Karaoke = ({ getOutOfTheDynamic }) => {
//     const { showMessage } = useFloatMessage()

//     const [errorMessage,setErrorMessage] = useState(null)


//     const ReturnToDashboard = async () => {
//         try {
//             await BLResetCurrentDynamic()
//             getOutOfTheDynamic()
//         } catch (error) {
            
//         }
//     }

//     const GetSongsData = async () => {
//         try {
//             // const response = await BLGetSongsData()

//             // if (response.success) {
//             //     setSongs(response.data)
//             // } else {
//             //     showMessage({
//             //         type: MESSAGE_TYPE.BAD,
//             //         text: response.message
//             //     })
//             // }

//             showMessage({
//                 type: MESSAGE_TYPE.SUCCESS,
//                 text: 'Iniciado con éxito'
//             })
//         } catch (error) {
            
//         }
//     }

//     const LoadPage = async () => {
//         try {
//             document.getElementById("root").classList.add("BodyComplete")
            
//             await GetSongsData()
//         } catch (error) {
            
//         }
//     }
 

//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <section>
//                 <p>Karaoke</p>
//             </section>
//         </SecondaryBase>
//     )
// }

// export { Karaoke };